import {
  ChevronDown,
  ChevronUp,
  Maximize,
  Minimize,
  Keyboard,
  MessageSquarePlus,
  NotebookPen,
  PanelsTopLeft,
  Scissors,
  Video,
  X,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { SubmitHandler } from "react-hook-form";
import { TacticalBoardEditor } from "@/features/canvas/TacticalBoardEditor";
import { TelestratorCanvas } from "@/features/canvas/TelestratorCanvas";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { TkticSelect } from "@/components/ui/TkticSelect";
import {
  emptyScene,
  sceneFromOnDisk,
  sceneToOnDisk,
  type BoardScene,
} from "@/lib/canvas/board-schema";
import {
  emptyDrawing,
  loadDrawing,
  saveDrawing,
  type DrawingV2,
} from "@/lib/canvas/telestrator-schema";
import { ApiError, errorMessage } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import type {
  CoachingClip,
  CoachingNote,
  CreateCoachingClipRequest,
  CreateCoachingNoteRequest,
  Match,
  Player,
  UpdateCoachingClipRequest,
  UpdateCoachingNoteRequest,
} from "@/lib/types";
import {
  Composer,
  coerceNoteCategory,
  parseTagsRaw,
  type ComposerHandle,
  type ComposerValues,
} from "./components/Composer";
import {
  ClipComposer,
  MAX_CLIP_DURATION_SECONDS,
  type ClipComposerHandle,
  type ClipComposerValues,
} from "./components/ClipComposer";
import {
  clearDraft,
  draftContextKey,
  isCurrentDraftContext,
  useComposerDraftContext,
  type DraftContext,
  loadDraft,
  saveDraft,
  type DraftScope,
} from "./composerDrafts";
import { coerceVisibility } from "@/features/coach/visibilityVocabulary";
import { useTeamSettings } from "@/features/coach/hooks/useTeamSettings";
import { getLastMatch, rememberLastMatch } from "./matchMemory";
import { suggestedPlayerIds } from "./suggestedPlayers";
import {
  defaultSlotForMatch,
  isSlotValidForMatch,
  SLOT_LABELS,
  type MatchSlot,
} from "@/features/matches/matchSlots";
import { MatchSlotPicker, matchLabel } from "./components/MatchSlotPicker";
import {
  SourceToggle,
  type CoachReviewSource,
} from "./components/SourceToggle";
import { TemplateSelector } from "./components/TemplateSelector";
import { ToolRail } from "./components/ToolRail";
import { ClipTrack } from "./components/ClipTrack";
import { ClipPresetBar } from "./components/ClipPresetBar";
import { FrameStepper } from "./components/FrameStepper";
import { NotesAndClipsRail } from "./components/NotesAndClipsRail";
import { RecentObservationsRail } from "./components/RecentObservationsRail";
import { ComposerDrawer } from "./components/ComposerDrawer";
import { FilmIcon, PencilIcon, PlusIcon } from "./components/icons";
import {
  clamp,
  formatClock,
  formatDuration,
  formatEditorTime,
  FRAME,
  type ClipWindow,
} from "./clipTrack";
import { CoachModal } from "@/features/coach/components/CoachModal";
import { useCoachReviewBundle } from "./hooks/useCoachReviewBundle";
import { useCoachNotes } from "./hooks/useCoachNotes";
import { useCoachClips } from "./hooks/useCoachClips";
import { useReviewKeyboardShortcuts } from "./hooks/useReviewKeyboardShortcuts";
import { useClipLoopPlayback } from "./hooks/useClipLoopPlayback";
import { useSaveCoachingNote } from "./hooks/useSaveCoachingNote";
import { useSaveCoachingClip } from "./hooks/useSaveCoachingClip";
import { useMatches } from "@/features/matches/useMatches";
import { useMe } from "@/features/auth/useMe";
import { isFocusModeEnabled } from "@/features/account/preferences";
import { getPublicNavCapabilities } from "@/features/navigation/navCapabilities";
import {
  formatPlayhead,
  useVideoPlayhead,
} from "@/features/playback/useVideoPlayhead";
import { useVideoKeyboardShortcuts } from "@/features/playback/useVideoKeyboardShortcuts";
import { FeedbackPlayerModal } from "@/features/playback/FeedbackPlayerModal";
import { OPERATIONAL_COACH_TABS } from "@/features/coach/CoachTabStrip";
import { canAuthorCoachFeedback } from "@/features/coach/coachWorkspaceAccess";
import { WorkspaceHeader } from "@/features/coach/components/WorkspaceHeader";
import { TeamScopeGate } from "@/features/navigation/TeamScopeGate";
import { useScopeStore } from "@/stores/scope";

/**
 * Coach Review — the unified shell for authoring video notes, video
 * clips, and tactical observations.
 *
 * Layout (M4) — mirrors the legacy Replay coach-review:
 *   - Header: title + bundle context + source toggle + focus-mode
 *     button + help button
 *   - Two columns at lg+: the LEFT column is the canvas stage
 *     (TacticalBoardEditor OR TelestratorCanvas, mode-switched by
 *     the source toggle); the RIGHT column is a single rail that
 *     stacks the canvas's built-in tool grid / color / stroke /
 *     formation controls AND, below them (passed via the canvas's
 *     `toolbarChildren` slot), the template selector + composer.
 *     The coach can type notes while watching the video / pitch.
 *   - Both surfaces get the SAME stage width (they differ only in
 *     height by aspect ratio) so the rail stays put on flip.
 *   - Focus mode (FocusModeOverlay below): the active canvas
 *     fills the viewport in a portaled overlay at <body>. The
 *     composer is hidden; tools are revealed on demand via a
 *     `<FloatingToolOverlay>` pill in the top-right corner.
 *     Mirrors legacy Replay focus-mode UX. Portal escape is
 *     load-bearing for the bounded stacking context the parent
 *     route's animation CSS creates.
 *   - Auth: M4 carries a localStorage Bearer-token placeholder.
 *     When the bundle 401s (or no token is stored), an inline
 *     `<InlineLoginPanel>` replaces the shell. Proper login UI
 *     lands in M9.
 *
 * Keyboard shortcuts: bound globally via useReviewKeyboardShortcuts.
 * Body-attribute scoping (SourceToggle sets/clears
 * `data-coach-review-source`) ensures shortcuts only fire when
 * Coach Review is the active route AND focus isn't in a form field.
 *
 * Save flow:
 *   - Composer submits its structured-text fields via onSubmit.
 *   - This component merges:
 *       - the form values (Zod-validated)
 *       - the canvas state (tactical_board_json OR drawing,
 *         depending on source)
 *       - the bundle's match context (match_id + null slot/
 *         timestamp for SP6; M5 wires HLS to provide the
 *         real timestamp_seconds)
 *   - Dispatches to useSaveCoachingNote (create OR update).
 *   - On success: invalidate the scoped cache (the hook does this)
 *     + reset the composer to fresh defaults (the SP5 key-bump
 *     pattern + clearing the template's lastAppliedId).
 *
 * Edit mode: when `note_id` is in the URL, the bundle returns the
 * note + auto-resolves its match. We pre-fill the composer +
 * canvas state from the seed and switch the save path to PATCH.
 */

type FreshComposerIntent = {
  kind: "note" | "clip";
  composing: boolean;
};

export function CoachReview() {
  const me = useMe();
  const context = useComposerDraftContext();
  const search = useSearch({ from: "/coach/review" });
  // Explicit "new clip/note" actions cross the edit-target remount. Carry
  // only their transient mode/open intent, never form content or an old owner.
  const [freshIntent, setFreshIntent] = useState<(FreshComposerIntent & {
    owner: string;
  }) | null>(null);
  const owner = draftContextKey(context);
  const isCreateTarget = search.note_id === undefined && search.clip_id === undefined;
  const createIntent = isCreateTarget && freshIntent?.owner === owner
    ? freshIntent : null;
  useEffect(() => {
    if (freshIntent && (freshIntent.owner !== owner || isCreateTarget)) {
      setFreshIntent(null);
    }
  }, [freshIntent, owner, isCreateTarget]);
  // Drop every form, override, canvas and preview synchronously across identity
  // or scope changes. Each departing editor keeps only its immutable context.
  const identity = JSON.stringify([
    draftContextKey(context),
    search.note_id ?? null,
    search.clip_id ?? null,
  ]);
  return <CoachReviewSession
    key={identity}
    draftContext={context}
    me={me}
    freshIntent={createIntent}
    onStartFresh={(intent) => setFreshIntent({ ...intent, owner })}
  />;
}

function CoachReviewSession({
  draftContext,
  me,
  freshIntent,
  onStartFresh,
}: {
  freshIntent: FreshComposerIntent | null;
  onStartFresh: (intent: FreshComposerIntent) => void;
  draftContext: DraftContext | null;
  me: ReturnType<typeof useMe>;
}) {
  const search = useSearch({ from: "/coach/review" });
  // Route navigation — opening a clip from the NotesAndClipsRail
  // pushes `?clip_id=` so the bundle re-seeds in clip-edit mode.
  const navigate = useNavigate({ from: "/coach/review" });
  // The parent reconciles /me once, including direct navigation/refresh.
  // Keep its observer outside the identity-keyed editor and auth gate.
  const focusModeEnabled = isFocusModeEnabled(me.data?.profile);
  // Multi-team users must pick a team before the scope-gated review
  // surface renders — see `<TeamScopeGate>`.
  const selectionRequired = useScopeStore((s) => s.selectionRequired);
  const draftTeamId = draftContext?.teamId ?? null;
  const bundle = useCoachReviewBundle({
    matchId: search.match_id,
    noteId: search.note_id,
    clipId: search.clip_id,
  });
  const saveNote = useSaveCoachingNote(draftContext);
  const saveClip = useSaveCoachingClip(draftContext);
  const toast = useToast();
  // Team-wide match list for the video-mode match picker (across
  // all the team's seasons — tktic#8).
  const matchesQuery = useMatches();

  // ---- Local canvas state ----
  // `source` seeds from the route's `?source=` intent (the M6a
  // creation-routing carry-in — a list page's "+ New observation"
  // routes here with `?source=tactical_board`). Absent ⇒ video,
  // the default authoring surface. Editing an existing note can
  // still auto-flip the source from the bundle (see the seed
  // effect below); the route intent is just the initial value.
  const [source, setSource] = useState<CoachReviewSource>(
    search.source ?? "video",
  );
  const [scene, setScene] = useState<BoardScene>(() => emptyScene());
  const [drawing, setDrawing] = useState<DrawingV2>(() => emptyDrawing());

  // ---- Video-mode match + slot anchor ----
  // A video coaching note is anchored to a match + slot (+ timestamp).
  // `matchId` seeds from the route's `?match_id=` when present (the
  // bundle resolved it); otherwise the coach picks one. `slot` seeds
  // to "full" but is corrected to the selected match's actual layout
  // by the effect below — a `two_halves` match has no `full` video.
  const [matchId, setMatchId] = useState<string>(search.match_id ?? "");
  const [slot, setSlot] = useState<string>("full");
  // Inline validation error when a video-mode save is attempted
  // without a match picked. Cleared on the next valid save.
  const [matchError, setMatchError] = useState<string | null>(null);
  // Set when the VOD heartbeat gets a 403 — the stream was killed
  // by an admin. The telestrator pauses the video itself; this
  // surfaces the "why" to the coach. Cleared when they pick a
  // different match (a fresh stream).
  const [streamKilled, setStreamKilled] = useState(false);
  // Keep matchId in sync if the route param changes (e.g. deep link).
  useEffect(() => {
    if (search.match_id) setMatchId(search.match_id);
  }, [search.match_id]);

  // The kill banner is keyed to the *current* match+slot — a fresh
  // stream gets a clean slate. The picker's `onMatchIdChange` also
  // clears it eagerly, but this effect covers the programmatic
  // paths the picker handler misses (a timeline-chip seek that
  // switches match, a deep-link route change). If the NEW match's
  // heartbeat also 403s, `useVodHeartbeat` re-raises the banner —
  // that's correct: a different killed stream is a new notice.
  useEffect(() => {
    setStreamKilled(false);
  }, [matchId, slot]);

  // ---- Composer mode: note vs. clip ----
  // Video mode hosts two composers — a coaching-NOTE composer and a
  // CLIP composer (a `[start, end]` window of the match video).
  // `composerMode` swaps which one the rail renders. Editing a clip
  // (`?clip_id=`) forces clip mode; editing a note (`?note_id=`)
  // forces note mode; otherwise the coach toggles. Tactical-board
  // mode has no clips — observations are notes only — so the toggle
  // is video-only and clip mode never applies there.
  const isClipEdit = search.clip_id !== undefined;
  const isNoteEdit = search.note_id !== undefined;
  const [composerMode, setComposerMode] = useState<"note" | "clip">(
    isClipEdit ? "clip" : freshIntent?.kind ?? "note",
  );
  // Keep the mode pinned to whichever edit the route demands.
  //
  // This runs on every `source` change too — not just on a route
  // change — because clip-edit must STAY clip mode. The tactical-
  // board source forces `note` mode (the board has no clips), and
  // the SourceToggle stays enabled under `?clip_id=`, so without
  // re-pinning a coach could go video → tactical-board → video and
  // land on the NOTE composer under a clip-edit URL — Save would
  // then write note data instead of updating the clip. Re-pinning
  // whenever the source is back on `video` closes that escape.
  useEffect(() => {
    if (source === "tactical_board") {
      // A clip is a video object — there is no board surface for it, so
      // clip-edit snaps the source straight back to video (the SourceToggle
      // stays enabled, so a coach CAN click the board tab; this reverts it
      // deterministically instead of relying on an incidental bundle
      // refetch to bounce it back, which starved under CI load).
      if (isClipEdit) {
        setSource("video");
        return;
      }
      // Observations are notes only — the board has no clip mode.
      setComposerMode("note");
      return;
    }
    // Video surface: the route param wins. Clip-edit pins clip
    // mode (including after a tactical-board round-trip);
    // note-edit pins note mode. A non-edit session leaves the
    // coach's manual toggle alone.
    if (isClipEdit) setComposerMode("clip");
    else if (isNoteEdit) setComposerMode("note");
  }, [isClipEdit, isNoteEdit, source]);

  // The full Match row for the picked id — needed by the
  // telestrator's `<HlsVideo>` (which builds stream URLs from
  // `match.team_id`). Resolve from the team-wide matches list
  // first; fall back to the bundle's resolved match (edit mode
  // hydrates the note's match there before the list lands).
  const selectedMatch = useMemo(
    () =>
      matchesQuery.data?.find((m) => m.id === matchId) ??
      bundle.data?.match ??
      undefined,
    [matchesQuery.data, matchId, bundle.data?.match],
  );

  // Keep `slot` consistent with the selected match's video layout.
  // The `slot` state seeds to "full", but a `two_halves` match has
  // no `full` video — leaving it there 404s the player. When a
  // match resolves whose layout doesn't include the current slot,
  // snap to a valid slot. The guard (only correct an INVALID slot)
  // means a slot the coach deliberately picked, or one seeded from a
  // note/clip being edited, is never clobbered — those are always
  // valid for their own match.
  //
  // A3: when correcting, prefer the deep-link `?slot=` ONCE if it's
  // valid for the match (GameShell's "Coach this match" link on a
  // two-halves game sends e.g. `&slot=second_half`). Folding the
  // deep-link preference into THIS effect — rather than a separate
  // seed effect — avoids a same-render race where this correction
  // would read the stale `slot` ("full") and snap to the default,
  // clobbering a separately-seeded value. The ref makes the deep-link
  // preference one-shot so the coach can switch slots freely after.
  const consumedSlotDeepLinkRef = useRef(false);
  useEffect(() => {
    if (!selectedMatch) return;
    if (!isSlotValidForMatch(selectedMatch, slot)) {
      const deepLink = search.slot;
      if (
        !consumedSlotDeepLinkRef.current &&
        !isNoteEdit &&
        !isClipEdit &&
        deepLink &&
        isSlotValidForMatch(selectedMatch, deepLink)
      ) {
        consumedSlotDeepLinkRef.current = true;
        setSlot(deepLink);
      } else {
        setSlot(defaultSlotForMatch(selectedMatch));
      }
    }
  }, [selectedMatch, slot, search.slot, isNoteEdit, isClipEdit]);

  // A3: remember the last match the coach worked on, per team, so a
  // later bare `/coach/review` mount can re-open on it (seeded by the
  // effect below). Persisted whenever a real match is active.
  useEffect(() => {
    if (matchId && draftTeamId) rememberLastMatch(draftTeamId, matchId);
  }, [matchId, draftTeamId]);

  // A3: on a BARE mount (no match/note/clip in the URL), seed the
  // picker from the remembered match — but only once, and only if the
  // remembered id still exists in the live matches list (a deleted
  // match must not leave a ghost selection). Ref-guarded so a later
  // match switch isn't undone, and gated on the matches list having
  // loaded.
  const seededMatchMemoryRef = useRef(false);
  useEffect(() => {
    if (seededMatchMemoryRef.current) return;
    if (isNoteEdit || isClipEdit) return;
    if (search.match_id || matchId) return; // URL/already-picked wins
    if (!draftTeamId) return;
    const matches = matchesQuery.data;
    if (!matches) return; // wait for the list
    seededMatchMemoryRef.current = true;
    const remembered = getLastMatch(draftTeamId);
    if (remembered && matches.some((m) => m.id === remembered)) {
      setMatchId(remembered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchesQuery.data, draftTeamId]);

  // ---- Stage size: single container-width budget ----
  //
  // `containerWidth` is the available width the canvas stage should
  // fill — the measured width of the stage `<section>`. Let the
  // measured width win even on narrow phones: `<main>` has horizontal
  // padding, so a 320px viewport can expose less than 320px of content.
  // Forcing a 320px inline stage there clips the right edge and hides
  // desktop-equivalent controls from mobile coaches.
  //
  // The section is marked `overflow-hidden` (see the JSX): the
  // canvas stage renders as a fixed-px, `flex-shrink-0` box at
  // `width={stageWidth}`, and without the clip an oversized stage
  // would inflate the section's own box, latching `containerWidth`
  // onto the largest size ever seen (a feedback loop that never
  // shrinks on resize). With `overflow-hidden` the section's box
  // stays exactly its flex-allocated width regardless of the
  // child, so observing it directly is stable.
  //
  // A CALLBACK ref (not `useRef` + a one-shot `useEffect`): the
  // section renders conditionally (behind the auth gate /
  // `selectionRequired` early-return). A `[]`-dep effect reading
  // `ref.current` would run once, find `null` if the section isn't
  // mounted yet, and never retry. The callback ref fires exactly
  // when the node attaches — same load-bearing pattern as
  // `useCanvasEnvelope`. `observe()` delivers an initial
  // measurement synchronously-ish, and every resize after.
  const [containerWidth, setContainerWidth] = useState(800);
  const stageMeasureObserver = useRef<ResizeObserver | null>(null);
  const stageMeasureRef = useCallback((node: HTMLElement | null) => {
    stageMeasureObserver.current?.disconnect();
    if (!node) {
      stageMeasureObserver.current = null;
      return;
    }
    const obs = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(Math.max(1, Math.floor(entry.contentRect.width)));
      }
    });
    obs.observe(node);
    stageMeasureObserver.current = obs;
  }, []);

  // ---- Focus mode ----
  // Declared early so the width budget below can read `focusOpen`.
  const [focusOpen, setFocusOpen] = useState(false);
  const focusButtonRef = useRef<HTMLButtonElement | null>(null);

  // ---- Composer drawer (v2) ----
  // The v2 layout moves the note / clip composer out of the canvas's
  // always-visible toolbar rail into a bottom slide-up drawer, opened
  // on demand by "Mark moment" / "Add details" (after "Make clip"
  // enters clip mode), and the M key. `composing`
  // is the open flag; the composer node mounts only while it's true (inline
  // mode) — closing unmounts it, which resets the form, matching the
  // standalone reference's "fresh form each open" behavior.
  //
  // Focus mode is unaffected: it still hosts the composer in its
  // floating tool panel (`composer={composerElement}` below). The
  // drawer is the INLINE surface only — opening focus mode closes
  // the drawer so the composer never mounts in two places at once.
  //
  // Edit mode (`?note_id=` / `?clip_id=`) is canvas-first: the coach
  // navigated here to see/edit the authored evidence. Details open
  // explicitly from the small Edit details action.
  const [composing, setComposing] = useState(freshIntent?.composing ?? false);
  const composeButtonRef = useRef<HTMLButtonElement | null>(null);

  // Edit deep links are canvas-first: the saved drawing / board scene
  // should be visible immediately, with details opened explicitly by
  // the "Edit details" action. Close must never tear down the canvas.

  // The stage fills the FULL width of its column.
  //
  // v1 subtracted a `CANVAS_TOOLBAR_BUDGET_PX` because the canvas
  // rendered its own 300px toolbar column inside this container. The
  // v2 restructure relocated the entire canvas toolbar into the
  // separate right-rail `<aside>` (via `toolRailSlot`), so the
  // canvas no longer has an in-container toolbar — subtracting the
  // budget would just leave a ~300px dead gap beside the video.
  //
  // `stageContainerRef`'s `<section>` is `flex-1` next to the 320px
  // aside, so `containerWidth` already IS the max width; the
  // `ResizeObserver` keeps it current, and `envelopeHeight` derives
  // 16:9 from it — so the stage stays max-width + aspect-correct on
  // every resize. Focus mode is the same (the overlay fills the
  // viewport).
  const stageWidth = useMemo(
    () => Math.max(1, containerWidth),
    [containerWidth],
  );

  // Both surfaces are now 16:9 and take the SAME width — the video
  // fills its 16:9 frame, and the tactical board is a 16:9 green
  // field with the pitch markings drawn inset at true proportions
  // (`TacticalBoardEditor` / `PitchBackground`). So a single
  // `stageWidth` drives both; no separate board sizing / letterbox.
  const videoStageWidth = stageWidth;
  const boardStageWidth = stageWidth;

  // ---- Seed scene/drawing/match anchor from bundle (edit mode) ----
  // Seed ONCE per edit target. The dep is the bundle note's object
  // IDENTITY, which changes on every refetch (staleTime expiry,
  // invalidateScope from a save) — re-running the seed then stomps
  // in-progress edit state: a coach who flipped the source toggle or
  // sketched on the board mid-edit got snapped back to the saved
  // note's surface/content (#360 surfaced this as a load-dependent
  // E2E flake). Same key pattern as `autoOpenedNoteKeyRef` below.
  const seededNoteKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!bundle.data?.note) {
      // Left edit mode (create-mode bundles carry no note) — drop
      // the key so RETURNING to the same note re-seeds.
      seededNoteKeyRef.current = null;
      return;
    }
    const note = bundle.data.note;
    const seedKey = String(note.id);
    if (seededNoteKeyRef.current === seedKey) return;
    seededNoteKeyRef.current = seedKey;

    if (note.note_context === "observation") {
      setSource("tactical_board");
      setComposerMode("note");
      // `tactical_board_json` is the on-disk shape (`stroke_width`).
      // `sceneFromOnDisk` converts it to the in-memory shape
      // (`strokeWidth`) the canvas + `sceneToOnDisk` round-trip
      // expects. Fall back to an empty scene if the blob is invalid.
      const parsed = note.tactical_board_json
        ? sceneFromOnDisk(note.tactical_board_json)
        : { ok: true as const, value: emptyScene() };
      setScene(parsed.ok ? parsed.value : emptyScene());
      setDrawing(emptyDrawing());
      return;
    }

    if (note.drawing && Object.keys(note.drawing).length > 0) {
      // `note.drawing` is the backend's on-disk shape (`type`-keyed).
      // `loadDrawing` converts it to the canonical in-memory shape
      // (`kind`-keyed) the canvas + `saveDrawing` round-trip expects.
      setDrawing(loadDrawing(note.drawing));
    } else {
      setDrawing(emptyDrawing());
    }
    // Editing a video note → seed the match + slot anchor so the
    // picker opens on the note's existing match/slot.
    if (note.note_context === "video") {
      setSource("video");
      setComposerMode("note");
      if (note.match_id) setMatchId(String(note.match_id));
      if (note.slot) setSlot(String(note.slot));
    }
  }, [bundle.data?.note]);

  // ---- Seed match anchor + drawing from bundle (clip edit mode) ----
  // Editing a clip (`?clip_id=`) — seed the match/slot picker on the
  // clip's anchor, and restore its drawing snapshot onto the canvas.
  // The clip's match/slot are immutable, so the picker is also
  // locked in clip-edit mode (see `MatchSlotPicker disabled` below).
  // Seed ONCE per clip — same refetch-stomp guard as the note seed.
  const seededClipKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const clip = bundle.data?.clip;
    if (!clip) {
      seededClipKeyRef.current = null;
      return;
    }
    const seedKey = String(clip.id);
    if (seededClipKeyRef.current === seedKey) return;
    seededClipKeyRef.current = seedKey;
    setSource("video");
    setComposerMode("clip");
    if (clip.match_id) setMatchId(String(clip.match_id));
    if (clip.slot) setSlot(String(clip.slot));
    if (clip.drawing && Object.keys(clip.drawing).length > 0) {
      // On-disk → in-memory, same as the note seed above.
      setDrawing(loadDrawing(clip.drawing));
    } else {
      setDrawing(emptyDrawing());
    }
  }, [bundle.data?.clip]);

  // ---- Composer default values ----
  // Seed new manual notes from the team's configured visibility.
  const teamSettingsQuery = useTeamSettings();
  const defaultNoteVisibility =
    teamSettingsQuery.data?.settings?.["notes.default_visibility"];

  const composerDefaults = useMemo<Partial<ComposerValues> | undefined>(() => {
    const note = bundle.data?.note;
    if (!note) {
      // Fresh mount (no edit-mode note). The M6a creation-routing
      // carry-in passes `?player_id=` so a "+ New observation"
      // routed from a Roster row opens with that player
      // preselected. Only that one field — everything else is the
      // composer's own DEFAULTS.
      //
      // `notes.default_visibility` is the team's own setting for
      // where a new note starts. It was stored, validated and served
      // by the API but never read by anything, so a team admin who
      // set it to Coach-only changed nothing. Seeding it here is what
      // makes that control real. Editing an existing note keeps the
      // note's own visibility — the setting is a starting point, not
      // a rewrite.
      const seeded: Partial<ComposerValues> = {};
      if (defaultNoteVisibility) seeded.visibility = defaultNoteVisibility;
      if (search.player_id) seeded.player_ids = [search.player_id];
      return Object.keys(seeded).length ? seeded : undefined;
    }
    return {
      title: note.title,
      note_type: note.note_type,
      // The API note payload types `category` as a free string;
      // narrow it to the composer's closed enum (off-list → "other",
      // matching the backend default).
      category: coerceNoteCategory(note.category),
      visibility: coerceVisibility(note.visibility),
      body: note.body ?? "",
      player_summary: note.player_summary ?? "",
      what_happened: note.what_happened ?? "",
      why_it_matters: note.why_it_matters ?? "",
      what_to_do_next: note.what_to_do_next ?? "",
      coach_private_note: note.coach_private_note ?? "",
      tags_raw: (note.tags ?? []).join(", "),
      player_ids: note.player_ids ?? [],
      // Observation event metadata — empty for video notes.
      event_title: note.event_title ?? "",
      event_date: note.event_date ?? "",
      event_type: note.event_type ?? "practice",
      // Reference video (#675) — editing a note that has one must not
      // silently drop it, same reason `body` is loaded here.
      ref_video_provider: note.ref_video_provider ?? "",
      ref_video_id: note.ref_video_id ?? "",
      ref_video_label: note.ref_video_label ?? "",
      ref_video_title: note.ref_video_title ?? "",
      ref_video_thumb: note.ref_video_thumb ?? "",
    };
  }, [bundle.data?.note, search.player_id, defaultNoteVisibility]);

  // Template-apply uses a key-bump on the composer so RHF picks up
  // the new defaults via the reset effect inside Composer.
  const [composerKey, setComposerKey] = useState(0);
  const [composerOverride, setComposerOverride] = useState<
    Partial<ComposerValues> | undefined
  >(undefined);

  // Imperative handle into the composer's RHF state. Two callers
  // need the live values (not the parent's stale defaults):
  //   - TemplateSelector's overwrite-protect check
  //   - the focus-mode portal transition, which snapshots the form
  //     before remounting the Composer inside the drawer
  // See ComposerHandle docstring for the full rationale.
  const composerRef = useRef<ComposerHandle | null>(null);
  const getLiveComposerValues = (): Partial<ComposerValues> =>
    composerRef.current?.getValues() ??
    composerOverride ??
    composerDefaults ??
    {};

  // ---- Clip composer state ----
  const clipComposerRef = useRef<ClipComposerHandle | null>(null);
  // Key-bump remounts the clip composer to re-seed defaults (after a
  // save, or on the focus-mode portal transition — see the focus
  // handlers).
  const [clipComposerKey, setClipComposerKey] = useState(0);
  // Override that survives a key-bump remount — the snapshot of
  // live clip-composer values taken before the focus-mode portal
  // transition, mirroring the note composer's `composerOverride`.
  // `undefined` means "use the computed defaults".
  const [clipComposerOverride, setClipComposerOverride] = useState<
    Partial<ClipComposerValues> | undefined
  >(undefined);

  // A1: seed the composer override from a persisted draft on mount —
  // this is what makes a draft survive a route navigation / refresh /
  // Quick-Feedback escalation hop. Create mode ONLY: edit mode is
  // seeded from the bundle by the seed effects above, and an edit
  // draft addresses its own `edit:<id>` key, so we never stomp a
  // bundle seed. Ref-guarded one-shot (same pattern as
  // `seededNoteKeyRef`) so a later `composerDefaults` recompute or an
  // `invalidateScope` refetch doesn't re-seed and clobber live edits.
  // The note draft merges OVER `composerDefaults` so a draft that only
  // touched the title keeps a `?player_id=` carry-in.
  //
  // The parent mounts a new editor only for a complete authorized context.
  // The captured context never changes during this editor's lifetime.
  const seededDraftRef = useRef(false);
  useEffect(() => {
    if (seededDraftRef.current) return;
    if (isNoteEdit || isClipEdit) return;
    if (!draftContext) return;
    seededDraftRef.current = true;
    const noteDraft = loadDraft({ ...draftContext, kind: "note" });
    if (noteDraft) {
      setComposerOverride({ ...(composerDefaults ?? {}), ...noteDraft });
    }
    const clipDraft = loadDraft({ ...draftContext, kind: "clip" });
    if (clipDraft) {
      setClipComposerOverride(clipDraft);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftContext]);

  // Frozen create-mode window. The playhead-derived `[t-5, t+8]`
  // window is captured into STATE once (on entering clip-create
  // mode, and after a save), NOT recomputed every render — a
  // per-tick memo would hand `ClipComposer` a fresh `defaultValues`
  // object on every `timeupdate`, and the composer resets its RHF
  // state whenever `defaultValues` changes, so the coach's
  // in-progress title / start / end / etc. would be wiped ~4×/s
  // during playback. `null` until clip-create mode is first
  // entered.
  const [clipCreateDefaults, setClipCreateDefaults] =
    useState<Partial<ClipComposerValues> | null>(null);
  // Focused feedback player modal — M5 coach-side scope. Opened from
  // the "Preview clip" / "Preview note" buttons in clip-edit /
  // note-edit mode (the bundle's seed clip / note is what's
  // previewed). Two independent flags — only one edit mode is
  // active at a time, but keeping them separate makes the render
  // condition for each modal explicit.
  const [clipPreviewOpen, setClipPreviewOpen] = useState(false);
  const [notePreviewOpen, setNotePreviewOpen] = useState(false);
  // Edit-mode clip defaults — hydrated from the bundle's seed clip.
  // Create-mode defaults are the frozen `clipCreateDefaults` state
  // above (NOT a per-render memo — see that state's comment).
  const clipEditDefaults = useMemo<
    Partial<ClipComposerValues> | undefined
  >(() => {
    const clip = bundle.data?.clip;
    if (!clip) return undefined;
    return {
      title: clip.title,
      description: clip.description ?? "",
      category: coerceNoteCategory(clip.category),
      visibility: coerceVisibility(clip.visibility),
      start_seconds: clip.start_seconds,
      end_seconds: clip.end_seconds,
      player_ids: clip.player_ids ?? [],
    };
  }, [bundle.data?.clip]);

  // Ref to the telestrator's backing `<video>` — forwarded to
  // whichever `<TelestratorCanvas>` is mounted (inline or focus
  // overlay). Drives the live time readout, the video keyboard
  // shortcuts, and — the M4 parity-audit P0-2 fix — the
  // `timestamp_seconds` a video note saves at.
  //
  // The element is tracked in STATE, not just a ref: the canvas
  // forwards it up through a callback ref deep inside
  // `<TelestratorCanvas>`, and nothing else would re-render
  // `CoachReview` when it mounts. `useVideoPlayhead` reads
  // `videoRef.current` at render time, so without a re-render it
  // would stay pinned to `null` and the readout would never leave
  // `--:--`. `setVideoEl` gives us that re-render; `videoElRef`
  // mirrors the state so the two hooks keep their `RefObject` API.
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  videoElRef.current = videoEl;
  const handleVideoElRef = useCallback((el: HTMLVideoElement | null) => {
    setVideoEl(el);
  }, []);
  const playhead = useVideoPlayhead(videoElRef);
  useVideoKeyboardShortcuts({ videoRef: videoElRef, source });

  // ---- v2 clip track ----
  // The v2 layout puts a draggable IN/OUT clip track in the main
  // content column. `clipWindow` (absolute seconds) is the single
  // source of truth shared by the ClipTrack, the ClipPresetBar, the
  // ThumbnailStrip, and the ClipComposer's start/end defaults.
  //
  // It's derived from / re-seeded by the same `[t-5, t+8]` window the
  // ClipComposer uses (`clipCreateDefaults` / `clipEditDefaults`), but
  // held in its own state so a track drag updates it live without
  // churning the composer's other fields (the composer is only
  // re-seeded with the window at drawer-open time — see
  // `clipComposerKey`).
  const [clipWindow, setClipWindow] = useState<ClipWindow | null>(null);
  // A match/slot switch in create mode replaces the backing media. Hold the
  // range empty until that replacement reports metadata; otherwise a render
  // between the picker change and the media load can re-seed from the old
  // recording's playhead and duration.
  const pendingClipRecordingReseedRef = useRef(false);
  const clipPlaybackActive = source === "video" && composerMode === "clip";
  // Media duration, tracked reactively off the backing `<video>` so
  // the clip track can map seconds → pixels. 0 until metadata loads.
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  useEffect(() => {
    const el = videoEl;
    if (!el) {
      setDuration(0);
      return;
    }
    const sync = () => {
      setDuration(Number.isFinite(el.duration) ? el.duration : 0);
    };
    sync();
    el.addEventListener("loadedmetadata", sync);
    el.addEventListener("durationchange", sync);
    return () => {
      el.removeEventListener("loadedmetadata", sync);
      el.removeEventListener("durationchange", sync);
    };
  }, [videoEl]);

  useEffect(() => {
    if (videoEl) videoEl.playbackRate = playbackRate;
  }, [playbackRate, videoEl]);

  const cyclePlaybackRate = useCallback(() => {
    const rates = [0.5, 1, 1.5, 2] as const;
    const currentIndex = rates.indexOf(playbackRate as (typeof rates)[number]);
    setPlaybackRate(rates[(currentIndex + 1) % rates.length]);
  }, [playbackRate]);

  // Deep-linking into a saved note should restore the same review
  // context as clicking the note on the timeline: load the match/slot,
  // seek the backing video to the saved timestamp, and show the saved
  // telestration over that frame. The video element can remount after
  // match/slot/source changes, so keep the pending seek separate from
  // the immediate click handler and apply it once the expected media is
  // mounted and metadata is available.
  const pendingVideoSeekRef = useRef<{
    matchId: string;
    slot: string;
    seconds: number;
  } | null>(null);

  const applyPendingVideoSeek = useCallback(() => {
    const pending = pendingVideoSeekRef.current;
    const video = videoElRef.current;
    if (!pending || !video) return false;
    if (pending.matchId !== matchId || pending.slot !== slot) return false;
    const hasMetadata =
      video.readyState >= HTMLMediaElement.HAVE_METADATA ||
      Number.isFinite(video.duration);
    if (!hasMetadata) return false;
    const max = Number.isFinite(video.duration)
      ? video.duration
      : pending.seconds;
    video.currentTime = clamp(pending.seconds, 0, max);
    video.pause();
    pendingVideoSeekRef.current = null;
    return true;
  }, [matchId, slot]);

  const queueVideoSeek = useCallback(
    (
      seconds: number,
      expectedMatchId: string | null | undefined,
      expectedSlot: string | null | undefined,
    ) => {
      if (!expectedMatchId || !expectedSlot) return;
      const normalizedSeconds = Math.max(0, seconds);
      pendingVideoSeekRef.current = {
        matchId: expectedMatchId,
        slot: expectedSlot,
        seconds: normalizedSeconds,
      };
      window.setTimeout(() => {
        void applyPendingVideoSeek();
      }, 0);
    },
    [applyPendingVideoSeek],
  );

  useEffect(() => {
    if (applyPendingVideoSeek()) return;
    const video = videoEl;
    if (!video) return;
    const onReady = () => {
      void applyPendingVideoSeek();
    };
    video.addEventListener("loadedmetadata", onReady);
    video.addEventListener("canplay", onReady);
    return () => {
      video.removeEventListener("loadedmetadata", onReady);
      video.removeEventListener("canplay", onReady);
    };
  }, [videoEl, applyPendingVideoSeek]);

  // Clip preview playback has fixed editor semantics: every Play starts at
  // IN and OUT wraps to IN until the coach pauses. Keeping this behavior
  // automatic prevents a stale playhead or hidden loop preference from
  // changing what the saved clip will contain.
  useClipLoopPlayback({
    video: videoEl,
    window: clipWindow,
    enabled: clipPlaybackActive,
  });

  // Compute a FRESH clip-create window from the CURRENT playhead —
  // `[playhead − 5s, playhead + 8s]`, clamped to `[0, duration]`
  // and capped at the 120s max (ports the legacy "Save Clip"
  // pre-fill). Called only at the moments a re-seed is wanted
  // (entering clip-create mode, after a save) — NOT every render —
  // so a `timeupdate` tick can't churn the clip composer's
  // `defaultValues` and wipe in-progress edits.
  const freshClipWindow = useCallback((): Partial<ClipComposerValues> => {
    const t = playhead.currentTime ?? 0;
    const dur = videoElRef.current?.duration;
    const hardMax = Number.isFinite(dur) ? (dur as number) : Infinity;
    const start = Math.max(0, t - 5);
    const end = Math.min(
      hardMax,
      Math.min(start + MAX_CLIP_DURATION_SECONDS, t + 8),
    );
    return {
      start_seconds: Number(start.toFixed(1)),
      end_seconds: Number(end.toFixed(1)),
      visibility: "team",
      category: "other",
    };
  }, [playhead]);
  const resetClipWindowToPlayhead = useCallback(() => {
    const w = freshClipWindow();
    const start = w.start_seconds ?? 0;
    const end = w.end_seconds ?? start;
    setClipCreateDefaults(w);
    setClipWindow({ start, end });
  }, [freshClipWindow]);

  // Seed the frozen create-mode window ONCE when clip-create mode
  // is first entered (video surface, clip mode, not a clip-edit).
  // It's captured at entry time and then held — the coach's
  // subsequent edits + playback ticks don't disturb it. A save
  // re-seeds it explicitly (see `handleClipSubmit`). `clipCreate-
  // Defaults` is also reset to `null` when clip-create mode is
  // left, so the NEXT entry takes a fresh window from wherever the
  // playhead is then, rather than a stale one from a prior session.
  const inClipCreateMode =
    source === "video" && composerMode === "clip" && !isClipEdit;
  const wasClipCreateModeRef = useRef(false);
  useEffect(() => {
    if (inClipCreateMode) {
      if (pendingClipRecordingReseedRef.current) return;
      setClipCreateDefaults((prev) => prev ?? freshClipWindow());
      // Seed the v2 clip track's window from the same `[t-5, t+8]`
      // window — once, on entry. A drag then owns it.
      setClipWindow((prev) => {
        if (prev) return prev;
        const w = freshClipWindow();
        return {
          start: w.start_seconds ?? 0,
          end: w.end_seconds ?? 0,
        };
      });
    } else if (wasClipCreateModeRef.current) {
      pendingClipRecordingReseedRef.current = false;
      setClipCreateDefaults(null);
      // Leaving clip-create mode drops the window so the next entry starts
      // clean.
      setClipWindow(null);
    }
    wasClipCreateModeRef.current = inClipCreateMode;
    // `freshClipWindow` changes identity with the playhead — but
    // it's only CALLED inside the `prev ?? …` guard, so once the
    // window is frozen a playhead tick re-running this effect is a
    // no-op (`prev` is non-null). Intentional: the effect re-runs,
    // the state doesn't change, no churn.
  }, [inClipCreateMode, freshClipWindow]);

  // Finish a recording-change reset only after the new source has a real
  // duration. `loadedmetadata` updates both the playhead and duration before
  // this effect runs, so `freshClipWindow` is calculated against the new
  // recording rather than the one the picker just replaced.
  useEffect(() => {
    if (
      !inClipCreateMode ||
      !pendingClipRecordingReseedRef.current ||
      duration <= 0
    ) {
      return;
    }
    pendingClipRecordingReseedRef.current = false;
    const next = freshClipWindow();
    setClipCreateDefaults(next);
    setClipWindow({
      start: next.start_seconds ?? 0,
      end: next.end_seconds ?? 0,
    });
  }, [duration, freshClipWindow, inClipCreateMode, matchId, slot]);

  const prepareClipRecordingChange = useCallback(() => {
    if (!inClipCreateMode) return;
    pendingClipRecordingReseedRef.current = true;
    videoElRef.current?.pause();
    setDuration(0);
    setClipCreateDefaults(null);
    setClipWindow(null);
  }, [inClipCreateMode]);

  // Clip-EDIT mode — seed the track window from the bundle's seed
  // clip (its `start_seconds` / `end_seconds`). Distinct path from
  // create mode: edit windows come from the persisted clip, not the
  // playhead.
  useEffect(() => {
    const clip = bundle.data?.clip;
    if (!isClipEdit || !clip) return;
    setClipWindow({
      start: clip.start_seconds,
      end: clip.end_seconds,
    });
  }, [isClipEdit, bundle.data?.clip]);

  // ---- Timeline chip rail (M4 audit → M5) ----
  // The rail under the video shows the selected match's video
  // notes. `useCoachNotes` is gated on `matchId` — no match, no
  // fetch (the rail renders its own no-match state). `activeNoteId`
  // tracks the last-seeked chip; it also feeds the Save Clip
  // `source_note_id` derivation in a later slice.
  const coachNotesQuery = useCoachNotes(matchId || undefined);
  const railNotes = coachNotesQuery.data?.notes ?? [];
  // The selected match's clips — feeds the clips rail + the
  // clip-track existing-clips lane. Same scope/match gating as
  // `useCoachNotes`; an `invalidateScope` from a clip save refetches.
  const coachClipsQuery = useCoachClips(matchId || undefined);
  // Tactical-board mode rail data rides the mount-time bundle (see the
  // single mount-time fetch rule in AGENTS.md) — no parallel fetch.
  const recentObservations = bundle.data?.recent_observations ?? [];
  const [activeNoteId, setActiveNoteId] = useState<number | null>(null);
  const [activeNoteMoment, setActiveNoteMoment] = useState<number | null>(null);

  // Click a chip → jump the video to that note's moment and
  // restore its drawing snapshot onto the canvas. Ports legacy
  // `seekCoachReviewNote`. When the note belongs to a different
  // match/slot than what's loaded, switch the picker first — the
  // `<HlsVideo>` remounts on the match change and the seek lands
  // once the new source loads (the `seeked`/`loadedmetadata`
  // subscription in `useVideoPlayhead` keeps the readout honest).
  const handleSeekNote = useCallback(
    (note: CoachingNote) => {
      setActiveNoteId(note.id);
      const target = Math.max(0, Number(note.timestamp_seconds ?? 0));
      setActiveNoteMoment(target);
      const targetMatchId = note.match_id ?? matchId;
      const targetSlot = note.slot ?? slot;
      if (note.match_id && note.match_id !== matchId) setMatchId(note.match_id);
      if (note.slot && note.slot !== slot) setSlot(note.slot);
      // Restore the note's drawing snapshot (or clear to empty).
      // `note.drawing` is the on-disk shape — `loadDrawing` converts
      // it to the in-memory shape the canvas expects.
      const nextDrawing =
        note.drawing && Object.keys(note.drawing).length > 0
          ? loadDrawing(note.drawing)
          : emptyDrawing();
      setDrawing(nextDrawing);
      queueVideoSeek(target, targetMatchId, targetSlot);
    },
    [matchId, queueVideoSeek, slot],
  );

  const openClipForEdit = useCallback(
    (clip: CoachingClip) => {
      const clipStart = Math.max(0, Number(clip.start_seconds ?? 0));
      setSource("video");
      setComposerMode("clip");
      setComposing(false);
      if (clip.match_id && clip.match_id !== matchId) setMatchId(clip.match_id);
      if (clip.slot && clip.slot !== slot) setSlot(clip.slot);
      setClipWindow({
        start: clip.start_seconds,
        end: clip.end_seconds,
      });
      setDrawing(
        clip.drawing && Object.keys(clip.drawing).length > 0
          ? loadDrawing(clip.drawing)
          : emptyDrawing(),
      );
      setActiveNoteId(null);
      setActiveNoteMoment(clipStart);
      queueVideoSeek(clipStart, clip.match_id, clip.slot);
      void navigate({
        to: "/coach/review",
        search: {
          match_id: clip.match_id,
          clip_id: clip.id,
          source: "video",
        },
      });
    },
    [matchId, navigate, queueVideoSeek, slot],
  );

  const autoOpenedNoteKeyRef = useRef<string | null>(null);
  const autoOpenedClipKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const clip = bundle.data?.clip;
    if (!clip) return;
    const key = [
      clip.id,
      clip.match_id,
      clip.slot,
      clip.start_seconds,
      clip.end_seconds,
    ].join(":");
    if (autoOpenedClipKeyRef.current === key) return;
    autoOpenedClipKeyRef.current = key;
    setSource("video");
    setComposerMode("clip");
    setActiveNoteId(null);
    setActiveNoteMoment(Math.max(0, Number(clip.start_seconds ?? 0)));
    queueVideoSeek(clip.start_seconds, clip.match_id, clip.slot);
  }, [bundle.data?.clip, queueVideoSeek]);

  useEffect(() => {
    const note = bundle.data?.note;
    if (!note || note.note_context !== "video") return;
    const key = [
      note.id,
      note.match_id ?? "",
      note.slot ?? "",
      note.timestamp_seconds ?? 0,
    ].join(":");
    if (autoOpenedNoteKeyRef.current === key) return;
    autoOpenedNoteKeyRef.current = key;
    setSource("video");
    setComposerMode("note");
    handleSeekNote(note);
  }, [bundle.data?.note, handleSeekNote]);

  /**
   * Rail click: seek to the note AND, in edit mode, retarget the edit.
   *
   * `handleSeekNote` only moves the playhead and local state. That is right
   * for plain review, but while editing (`?note_id=`) the composer reads
   * `bundle.data.note`, which is keyed off the URL — so switching notes via
   * the rail left the coach editing the note they arrived on while the video
   * showed a different one. Saving would have written the new content over
   * the old note.
   *
   * The navigate lives HERE rather than inside `handleSeekNote` because the
   * bundle-load effect above calls that function; navigating from it would
   * reload the bundle and re-fire the effect.
   */
  const handleRailNoteClick = useCallback(
    (note: CoachingNote) => {
      if (isNoteEdit && note.id !== search.note_id) {
        // While the composer is open this routes through `requestNoteSwitch`
        // (declared below) via the ref, so a rail click closes the drawer and
        // prompts about unsaved edits instead of switching underneath them.
        // Both paths go through the same switch logic so the stale-override
        // clearing in `goToNote` can't be bypassed — a rail click with the
        // composer CLOSED still carries a snapshot from the note being left.
        requestNoteSwitchRef.current?.(note);
        return; // the bundle effect seeks once the new note lands
      }
      handleSeekNote(note);
    },
    [composing, handleSeekNote, isNoteEdit, navigate, search.note_id],
  );

  /**
   * Switch the note being edited from INSIDE the composer.
   *
   * The composer drawer is `position: fixed` and covers the viewport, so the
   * page's notes rail is unreachable while composing — a click there lands on
   * the drawer, which is why switching notes appeared to do nothing and the
   * composer kept showing the note the coach arrived on. Same class of
   * problem as #366 (the drawer covering the source tabs), and the same
   * remedy: put the control in the drawer header.
   *
   * Unsaved edits are the coach's, not ours to throw away, so a dirty form
   * prompts instead of switching silently.
   */
  const [pendingNoteSwitch, setPendingNoteSwitch] =
    useState<CoachingNote | null>(null);

  const goToNote = useCallback(
    (note: CoachingNote) => {
      setPendingNoteSwitch(null);
      setComposing(false);
      // Closing the composer snapshots its live values into
      // `composerOverride` so in-progress work survives a close/reopen — and
      // that override OUTRANKS `composerDefaults`. Carrying it across a note
      // switch is what made the editor keep showing the previous note's text
      // even once the URL had moved on. The snapshot belongs to the note we
      // are leaving, so drop it (and force a fresh mount) on the way out.
      setComposerOverride(undefined);
      setComposerKey((k) => k + 1);
      void navigate({
        to: "/coach/review",
        search: (prev) => ({ ...prev, note_id: note.id }),
      });
    },
    [navigate],
  );

  const requestNoteSwitch = useCallback(
    (note: CoachingNote) => {
      if (note.id === search.note_id) return;
      if (composerRef.current?.isDirty()) {
        setPendingNoteSwitch(note);
        return;
      }
      goToNote(note);
    },
    [goToNote, search.note_id],
  );

  // `handleRailNoteClick` is declared above this (it feeds the clip track and
  // the rail), so it reaches the switch logic through a ref rather than being
  // reordered — the surrounding declarations are order-sensitive.
  const requestNoteSwitchRef = useRef(requestNoteSwitch);
  requestNoteSwitchRef.current = requestNoteSwitch;

  // ---- v2 clip-track handlers ----
  // Seek the backing `<video>` to an absolute time. The clip track,
  // preset bar, thumbnail strip, and frame stepper all route through
  // this so there's one seek path; `useVideoPlayhead` re-renders the
  // readouts off the resulting `seeked` event.
  const seekVideo = useCallback((seconds: number) => {
    const v = videoElRef.current;
    if (!v) return;
    const dur = Number.isFinite(v.duration) ? v.duration : Infinity;
    v.currentTime = clamp(seconds, 0, dur);
  }, []);

  const DRAWING_FRAME_TOLERANCE_SECONDS = 0.5;
  // The moment the canvas drawing is anchored to:
  //   - clip mode → the clip window's FIRST frame. A clip's drawing
  //     is a freeze-frame over its first frame — the same contract
  //     the feedback player replays (it shows the clip drawing only
  //     while paused at `start_seconds` and hides it on Play). The
  //     anchor follows the live window, so dragging the IN handle
  //     re-anchors instead of staying pinned to a stale moment.
  //   - note mode → the active saved note's moment (null while the
  //     coach is free-drawing a new note at the paused frame).
  const videoDrawingAnchor =
    source === "video" && composerMode === "clip"
      ? (clipWindow?.start ?? null)
      : activeNoteMoment;
  const showVideoDrawing =
    source !== "video" ||
    videoDrawingAnchor == null ||
    (playhead.paused &&
      (playhead.currentTime == null ||
        Math.abs(playhead.currentTime - videoDrawingAnchor) <=
          DRAWING_FRAME_TOLERANCE_SECONDS));

  // Release a loaded note's pin once the coach parks somewhere else.
  // Clicking a note mark/chip loads its drawing pinned to its moment
  // and the canvas hides that drawing away from the pinned frame.
  // Without a release the pin outlives the visit: the coach scrubs
  // to a new moment and every new stroke joins the HIDDEN saved
  // drawing — "can't telestrate again". When the playhead settles
  // (paused) outside the tolerance, drop the pin and start a fresh
  // canvas. Skipped:
  //   - in note-edit mode (the pin IS the note being edited — its
  //     drawing must survive a scrub-away + return);
  //   - in clip mode (the anchor derives from the clip window);
  //   - while a queued seek is in flight (chip clicks across
  //     match/slot land late — the stale playhead isn't a "park").
  // Reads the LIVE element time rather than playhead state: a just-
  // applied pending seek updates `video.currentTime` synchronously
  // but the `seeked` event (which refreshes playhead state) lands a
  // tick later — state alone could release a pin that was satisfied.
  useEffect(() => {
    if (source !== "video" || composerMode === "clip" || isNoteEdit) return;
    if (activeNoteMoment == null) return;
    if (pendingVideoSeekRef.current) return;
    const v = videoElRef.current;
    if (!v || !v.paused) return;
    const t = v.currentTime;
    if (!Number.isFinite(t)) return;
    if (Math.abs(t - activeNoteMoment) <= DRAWING_FRAME_TOLERANCE_SECONDS) {
      return;
    }
    setActiveNoteId(null);
    setActiveNoteMoment(null);
    setDrawing(emptyDrawing());
    // playhead.paused/currentTime are the re-run triggers (they tick
    // on seeked/pause); the live element is the source of truth.
  }, [
    source,
    composerMode,
    isNoteEdit,
    activeNoteMoment,
    playhead.paused,
    playhead.currentTime,
  ]);

  // Apply a clip window from a drag / preset, clamped to the media.
  const applyClipWindow = useCallback(
    (next: ClipWindow) => {
      const dur = duration > 0 ? duration : Infinity;
      setClipWindow({
        start: clamp(next.start, 0, dur),
        end: clamp(next.end, 0, dur),
      });
    },
    [duration],
  );

  // Set the clip IN point at the current playhead — keeps OUT at
  // least 5 frames ahead. Shared by the `I` shortcut + the preset bar.
  const setClipIn = useCallback(() => {
    const t = playhead.currentTime ?? 0;
    setClipWindow((prev) => {
      const end = prev ? Math.max(prev.end, t + FRAME * 5) : t + 0.5;
      return { start: t, end };
    });
  }, [playhead]);

  // Set the clip OUT point at the current playhead — keeps IN at
  // least 5 frames behind. When that minimum-separation clamp has to
  // MOVE the IN point, flag the park-suppression ref: setting OUT
  // must leave the playhead on the OUT frame (the coach is looking
  // at the frame they're cutting at), but the clamp's incidental
  // `start` change would otherwise fire the first-frame parking
  // effect below and yank the playhead away.
  const suppressClipParkRef = useRef(false);
  const setClipOut = useCallback(() => {
    const t = playhead.currentTime ?? 0;
    const prev = clipWindow;
    const start = Math.max(
      0,
      prev ? Math.min(prev.start, t - FRAME * 5) : Math.max(0, t - 0.5),
    );
    if (!prev || start !== prev.start) suppressClipParkRef.current = true;
    setClipWindow({ start, end: t });
  }, [playhead, clipWindow]);

  // Park the playhead on the clip's FIRST frame whenever the IN
  // boundary moves (preset, `I` key, handle drag, edit load). A
  // clip's telestration is a freeze-frame over its first frame —
  // parking there makes the canvas immediately visible/editable
  // (see `videoDrawingAnchor`) and previews exactly the frame the
  // feedback player will hold before Play. Keyed on the start VALUE
  // so loop bounces and OUT-handle drags don't re-seek; `setClipOut`
  // suppresses the park when its separation clamp moves `start` as a
  // side effect (the flag is only raised when `start` changes, so a
  // consumed-or-never-raised flag can't leak into later parks).
  const clipWindowStart = clipWindow?.start;
  useEffect(() => {
    if (source !== "video" || composerMode !== "clip") return;
    if (clipWindowStart == null) return;
    if (suppressClipParkRef.current) {
      suppressClipParkRef.current = false;
      return;
    }
    seekVideo(clipWindowStart);
  }, [source, composerMode, clipWindowStart, seekVideo]);

  // Explicit "back to the first frame". Setting OUT keeps the
  // playhead on the out frame (the coach needs to see what they're
  // cutting at), so this is the deliberate way back to the freeze
  // frame the clip's telestration is anchored to. The preset-bar
  // button and the `[` shortcut share this one path.
  const goToClipFirstFrame = useCallback(() => {
    if (clipWindow) seekVideo(clipWindow.start);
  }, [clipWindow, seekVideo]);

  const handleTemplateApply = (
    values: Partial<ComposerValues> & { __appliedTemplateId: string },
  ) => {
    const { __appliedTemplateId: _unused, ...fields } = values;
    void _unused;
    // Build the override on top of the LIVE form state so the
    // coach's unsaved edits in fields the template doesn't write
    // (visibility, tags, player_ids, coach_private_note) survive
    // the template apply. Templates only overwrite their declared
    // fields; everything else stays.
    const live = composerRef.current?.getValues();
    setComposerOverride({ ...(live ?? composerDefaults ?? {}), ...fields });
    setComposerKey((k) => k + 1);
  };

  // ---- Help popover state ----
  // (focusOpen + focusButtonRef declared earlier so width budget can
  // read focusOpen.)
  const [helpOpen, setHelpOpen] = useState(false);

  // M7.5 B7: Escape closes the popover (standard dialog behavior).
  useEffect(() => {
    if (!helpOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setHelpOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [helpOpen]);

  // Snapshot whichever composer is currently live into its
  // override, so it survives the focus-mode portal remount. The
  // rail hosts EITHER the note/observation composer (`composerRef`)
  // OR the clip composer (`clipComposerRef`) depending on
  // `composerMode` — in clip mode `composerRef.current` is null and
  // the live form is behind `clipComposerRef`, so a note-only
  // snapshot would silently drop the coach's clip edits across the
  // transition. Snapshot the one that's mounted.
  // A1: the draft scope for a given composer mode. Edit mode keys the
  // draft to the note/clip id (`?note_id=` / `?clip_id=`) so an edit
  // draft never bleeds into the create form; create mode keys to
  // `create`. The captured author/club/team/season context owns every key.
  const draftScopeFor = (mode: "note" | "clip"): DraftScope | null => {
    if (!draftContext) return null;
    // `note_id` / `clip_id` are coerced to numbers by the search
    // schema; stringify for the draft key (undefined → create mode).
    const editId = mode === "clip" ? search.clip_id : search.note_id;
    return {
      ...draftContext,
      kind: mode,
      editId: editId === undefined ? null : String(editId),
    };
  };

  // A1: storage-only persist of whichever composer is live. No state
  // setters here so it's safe to call during unmount (a React state
  // update on an unmounting tree is a no-op + warning). Returns the
  // live values so the snapshot wrapper can also seed the override.
  const persistActiveDraft = (): void => {
    if (composerMode === "clip") {
      const clipComposer = clipComposerRef.current;
      const liveClip = clipComposer?.getValues();
      if (clipComposer && liveClip && clipComposer.isDirty()) {
        saveDraft(draftScopeFor("clip"), liveClip);
      }
    } else {
      const composer = composerRef.current;
      const liveNote = composer?.getValues();
      if (composer && liveNote && composer.isDirty()) {
        saveDraft(draftScopeFor("note"), liveNote);
      }
    }
  };

  // Only mode/form values can change in this editor. Its identity, scope and
  // edit target are immutable; never read a new actor from an old cleanup.
  const persistActiveDraftRef = useRef(persistActiveDraft);
  persistActiveDraftRef.current = persistActiveDraft;
  // Layout cleanup runs before child imperative form refs detach. Pagehide
  // covers document refresh; beforeunload snapshots before a cancelled leave.
  // Explicit logout invalidates this captured context before any cleanup runs.
  useLayoutEffect(() => {
    const persist = () => persistActiveDraftRef.current();
    window.addEventListener("pagehide", persist);
    window.addEventListener("beforeunload", persist);
    return () => {
      persist();
      window.removeEventListener("pagehide", persist);
      window.removeEventListener("beforeunload", persist);
    };
  }, []);

  const snapshotActiveComposer = () => {
    if (composerMode === "clip") {
      const clipComposer = clipComposerRef.current;
      const liveClip = clipComposer?.getValues();
      if (liveClip) {
        setClipComposerOverride(liveClip);
        // A1: persist dirty forms so the clip draft survives navigation
        // / refresh. Untouched defaults and post-save resets must not
        // resurrect as stored drafts.
        if (clipComposer && clipComposer.isDirty()) {
          saveDraft(draftScopeFor("clip"), liveClip);
        }
      }
    } else {
      const composer = composerRef.current;
      const liveNote = composer?.getValues();
      if (liveNote) {
        setComposerOverride(liveNote);
        // A1: persist dirty forms so the note draft survives navigation
        // / refresh. Untouched defaults and post-save resets must not
        // resurrect as stored drafts.
        if (composer && composer.isDirty()) {
          saveDraft(draftScopeFor("note"), liveNote);
        }
      }
    }
  };

  // Open focus mode: snapshot live form values into the override so
  // when the inline composer remounts on close, it picks up any
  // edits the coach made BEFORE clicking focus. The composer node
  // is re-parented into the focus-mode floating panel while focus
  // is open (it's NOT unmounted), but the snapshot is the
  // symmetric bookend to `closeFocus` and guards the inline form's
  // continuity.
  const openFocus = () => {
    if (!focusModeEnabled) return;
    snapshotActiveComposer();
    // `focusOpen` removes the regular drawer before the Focus portal
    // mounts, so an already-open draft can remain open without ever
    // double-mounting the form.
    setFocusOpen(true);
  };
  const closeFocus = () => {
    // Snapshot the live composer values BEFORE flipping focusOpen.
    // The composer node lives inside the focus-mode floating panel
    // while focus mode is open; closing unmounts it and remounts a
    // fresh instance inline. Without this snapshot any edits the
    // coach made inside focus mode (in EITHER composer) are lost on
    // exit.
    snapshotActiveComposer();
    setComposing(false);
    setFocusOpen(false);
    // Return focus to the trigger on close. We do it after the
    // state flip so the inline composer has remounted (it's
    // unmounted while focusOpen) and the focus call lands inside
    // a regular non-inert subtree.
    queueMicrotask(() => focusButtonRef.current?.focus());
  };
  useEffect(() => {
    if (!focusModeEnabled && focusOpen) {
      closeFocus();
    }
  }, [closeFocus, focusModeEnabled, focusOpen]);

  // ---- Composer drawer open / close (v2) ----
  // Opening for a note (`M` / "Mark moment") still opens the drawer
  // immediately. Clip authoring is a two-step flow: first
  // enter clip mode so the IN/OUT controls remain visible, then open
  // the drawer when the coach is ready to save details.
  const openComposerNote = () => {
    if (!isClipEdit) setComposerMode("note");
    setComposing(true);
  };
  const enterClipMode = () => {
    // Clip mode is video-only; the tactical board has no clips.
    if (source !== "video") {
      openComposerNote();
      return;
    }
    if (isNoteEdit) return;
    // Creating a clip intentionally includes five seconds of lead-in, but
    // entering the editor must leave the video on the frame the coach chose.
    // The normal IN-boundary effect parks playback on the first frame after a
    // manual trim; suppress that seek for this initial window only.
    if (!isClipEdit) suppressClipParkRef.current = true;
    setComposerMode("clip");
    if (!isClipEdit) setComposing(false);
  };
  const openComposerClip = () => {
    // Clip mode is video-only; the tactical board has no clips.
    if (source === "video" && !isNoteEdit) setComposerMode("clip");
    setComposing(true);
  };
  const exitClipMode = () => {
    if (isNoteEdit || isClipEdit) return;
    setComposerMode("note");
    setComposing(false);
    setClipWindow(null);
    setClipCreateDefaults(null);
    setClipComposerOverride(undefined);
    // A1: "Exit clip mode" is an explicit cancel — drop the persisted
    // clip draft so it doesn't resurrect on the next mount.
    clearDraft(draftScopeFor("clip"));
  };
  const startNewClipFromEdit = () => {
    onStartFresh({ kind: "clip", composing: false });
    const targetMatchId =
      matchId || bundle.data?.clip?.match_id || search.match_id || "";
    setSource("video");
    setComposerMode("clip");
    setComposing(false);
    setClipComposerOverride(undefined);
    setClipComposerKey((k) => k + 1);
    setClipCreateDefaults(null);
    setClipWindow(null);
    setDrawing(emptyDrawing());
    setActiveNoteId(null);
    setActiveNoteMoment(null);
    void navigate({
      to: "/coach/review",
      search: targetMatchId
        ? { match_id: targetMatchId, source: "video" }
        : { source: "video" },
    });
  };
  const startNewNoteFromEdit = () => {
    onStartFresh({ kind: "note", composing: true });
    const targetMatchId =
      matchId || bundle.data?.note?.match_id || search.match_id || "";
    setSource("video");
    setComposerMode("note");
    setComposerOverride({});
    setComposerKey((k) => k + 1);
    setComposing(true);
    setDrawing(emptyDrawing());
    setActiveNoteId(null);
    setActiveNoteMoment(null);
    void navigate({
      to: "/coach/review",
      search: targetMatchId
        ? { match_id: targetMatchId, source: "video" }
        : { source: "video" },
    });
  };
  const startNewObservationFromEdit = () => {
    onStartFresh({ kind: "note", composing: true });
    setSource("tactical_board");
    setComposerMode("note");
    setComposerOverride({});
    setComposerKey((k) => k + 1);
    setComposing(true);
    setScene(emptyScene());
    setActiveNoteId(null);
    setActiveNoteMoment(null);
    void navigate({
      to: "/coach/review",
      search: { source: "tactical_board" },
    });
  };
  const closeComposer = () => {
    // Snapshot the live form in EVERY mode, not just edit. The
    // drawer unmounts its children on close, and the tactical-board
    // workflow is open-drawer → type → close → draw on the board →
    // reopen; without the snapshot, create mode silently dropped
    // everything typed on that round trip. The post-save reset
    // clears the snapshot and resets the live form, so a SAVED note
    // reopens at defaults — except the M7.5 B8 sticky fields
    // (note_type / category / visibility / player_ids), which
    // intentionally carry over so a coach can save several notes on
    // one player without re-tagging.
    snapshotActiveComposer();
    setComposing(false);
    if (isNoteEdit || isClipEdit) return;
    // Return focus to whichever trigger is in the stage control bar.
    queueMicrotask(() => composeButtonRef.current?.focus());
  };

  // ---- Source switching (shared path) ----
  // The page tabs, the `V`/`B` shortcuts, and the drawer's compact
  // toggle (#366) all flip the source through here: snapshot the
  // live composer first so the canvas remount keeps unsaved edits.
  const switchSource = useCallback(
    (next: CoachReviewSource) => {
      if (next === source) return;
      const live = composerRef.current?.getValues();
      if (live) setComposerOverride(live);
      setSource(next);
    },
    [source],
  );

  // ---- Keyboard shortcuts ----
  // The tool letters + `?` + `Esc` are handled inside the hook;
  // here we wire the v2 review-action keys to Coach Review state.
  // `C` enters clip range mode, `M` opens the mark-moment drawer, and
  // `I` / `O` set the clip window. On the tactical board `C` falls
  // back to a note open (no clips there). Frame step routes through the same
  // `seekVideo` the FrameStepper buttons use.
  useReviewKeyboardShortcuts({
    onToggleHelp: () => setHelpOpen((v) => !v),
    onEnterClip: () => enterClipMode(),
    onMarkMoment: () => openComposerNote(),
    onSetClipIn: () => {
      if (source === "video" && composerMode === "clip") setClipIn();
    },
    onSetClipOut: () => {
      if (source === "video" && composerMode === "clip") setClipOut();
    },
    onGoToClipIn: () => {
      if (source === "video" && composerMode === "clip") goToClipFirstFrame();
    },
    onFrameStep: (direction, shift) => {
      const v = videoElRef.current;
      if (!v) return;
      const delta = (shift ? 1 : FRAME) * direction;
      seekVideo(v.currentTime + delta);
    },
    // `F` — toggle focus mode (the same open/close the Focus button
    // drives).
    onToggleFocus: () => {
      if (!focusModeEnabled) return;
      if (focusOpen) closeFocus();
      else openFocus();
    },
    // `V` / `B` — switch the active source surface.
    onSwitchSource: switchSource,
  });

  // ---- Save handler ----
  // Blank-wipe guard: the PATCH always carries the CURRENT board
  // scene, so updating an observation whose saved board had content
  // while the live board is empty (Clear Board, a not-yet-seeded
  // scene) silently destroys the drawing. Park the submitted values
  // and confirm before letting that save through.
  const [pendingWipeValues, setPendingWipeValues] =
    useState<ComposerValues | null>(null);
  const handleSubmit: SubmitHandler<ComposerValues> = async (values) => {
    if (search.note_id && source === "tactical_board") {
      const liveBoardEmpty =
        scene.tokens.length === 0 && scene.shapes.length === 0;
      const original = bundle.data?.note?.tactical_board_json;
      const parsedOriginal = original ? sceneFromOnDisk(original) : null;
      const savedBoardHasContent = parsedOriginal?.ok
        ? parsedOriginal.value.tokens.length +
            parsedOriginal.value.shapes.length >
          0
        : false;
      if (liveBoardEmpty && savedBoardHasContent) {
        setPendingWipeValues(values);
        return;
      }
    }
    await submitNoteValues(values);
  };

  const submitNoteValues = async (values: ComposerValues) => {
    if (!isCurrentDraftContext(draftContext)) return;
    // The note CONTEXT is driven by the active SOURCE, not by
    // whether a match happens to be in scope:
    //   - video surface          → a video coaching note
    //   - tactical_board surface → an observation note
    // (This was previously keyed off `bundle.data?.match`, which
    // meant video mode with no preloaded match silently saved an
    // observation — wrong.)
    const isObservation = source === "tactical_board";

    // Merge canvas data based on the active source.
    // tactical_board_json + drawing are SEPARATE columns on
    // coaching_notes, so a single POST/PATCH can carry both. We
    // only emit the field for the active source — the inactive
    // surface's content stays in memory but doesn't ride along
    // (an unused empty scene/drawing in the payload is harmless,
    // but skipping keeps wire traffic + the audit log small).
    // Both canvas blobs are the canonical IN-MEMORY shapes; the
    // backend stores the ON-DISK shapes. The serializers do the
    // boundary rename:
    //   - `saveDrawing`: telestrator `kind`→`type`,
    //     `strokeWidth`→`width`. Skipping it sends `kind`-keyed
    //     objects, which the backend HARD-rejects with a 422
    //     ("drawing object type must be one of …").
    //   - `sceneToOnDisk`: tactical-board `strokeWidth`→
    //     `stroke_width`. Skipping it doesn't 422 (the board
    //     validator is allowlist-style) — it SILENTLY drops the
    //     stroke width, so a thick/thin board shape saves at the
    //     default-3 fallback.
    const canvasFields =
      source === "tactical_board"
        ? { tactical_board_json: sceneToOnDisk(scene) }
        : {
            drawing: saveDrawing(drawing) as unknown as Record<string, unknown>,
          };

    // Video notes need a match anchor. Block the save with a
    // visible error rather than letting the backend 422.
    if (!isObservation && !matchId) {
      setMatchError("Pick a match before saving a video note.");
      return;
    }
    setMatchError(null);

    if (search.note_id) {
      // PATCH path. `UpdateCoachingNoteRequest` accepts the
      // source/anchor/event fields too — the backend applies only
      // what we send (`model_dump(exclude_unset=True)`) and
      // revalidates the MERGED row. So the PATCH must carry the
      // active source's context the same way POST does, or:
      //   - editing an observation's event_title/date/type silently
      //     wouldn't persist, and
      //   - switching video ↔ tactical board on an existing note
      //     would leave the stale `note_context` + anchor.
      //
      // `body` carries the composer's long-form notes field — it
      // used to be hardcoded `""` here, which silently wiped the
      // stored `body` on every edit (M4 parity-audit P0-1).
      const originalContext = bundle.data?.note?.note_context;
      const isContextFlip =
        (isObservation ? "observation" : "video") !== originalContext;

      const contextFields: Partial<UpdateCoachingNoteRequest> = isObservation
        ? {
            note_context: "observation",
            // Observation event metadata — persist the composer's
            // event fields.
            event_title: values.event_title,
            event_date: values.event_date,
            event_type: values.event_type,
            // On a video→observation FLIP, clear the match anchor.
            // The stored match_id/slot/timestamp are NOT inert:
            // `db.list_coaching_notes(match_id=...)` filters purely
            // on match_id (no note_context guard), so a converted
            // observation that kept its match_id would still show
            // up in that match's note lists/timelines. POSTed
            // observations use null anchors — a flipped one must
            // match. (A note that was always an observation never
            // had anchors, so we only send the nulls on a flip.)
            ...(isContextFlip
              ? { match_id: null, slot: null, timestamp_seconds: null }
              : {}),
          }
        : {
            note_context: "video",
            // A video note needs the match anchor. Always send
            // match_id + slot so an observation→video flip lands a
            // coherent row (the merged-state validator requires
            // all three). `timestamp_seconds` is only sent on a
            // context FLIP — for an in-place video edit we must
            // NOT clobber the note's stored timestamp with 0.
            match_id: matchId,
            slot,
            ...(isContextFlip ? { timestamp_seconds: 0 } : {}),
          };

      const body: UpdateCoachingNoteRequest = {
        title: values.title,
        body: values.body,
        category: values.category,
        visibility: values.visibility,
        note_type: values.note_type,
        player_summary: values.player_summary,
        what_happened: values.what_happened,
        why_it_matters: values.why_it_matters,
        what_to_do_next: values.what_to_do_next,
        coach_private_note: values.coach_private_note,
        player_ids: values.player_ids,
        tags: parseTagsRaw(values.tags_raw),
        // Empty string -> null so a cleared reference actually clears the
        // columns. The API rejects a half-written pair, so these travel
        // together or not at all.
        ref_video_provider: values.ref_video_provider || null,
        ref_video_id: values.ref_video_id || null,
        ref_video_label: values.ref_video_label || null,
        ref_video_title: values.ref_video_title || null,
        ref_video_thumb: values.ref_video_thumb || null,
        ...contextFields,
        ...canvasFields,
      };
      try {
        await saveNote.mutateAsync({
          mode: "update",
          id: search.note_id,
          body,
        });
      } catch (err) {
        // Keep the form intact so the coach can retry — skip the
        // post-save reset below. The inline error block stays as
        // secondary detail; the toast is the at-a-glance signal.
        toast.error(
          isObservation ? "Couldn't save observation" : "Couldn't save note",
          { description: errorMessage(err) },
        );
        return;
      }
      toast.success(isObservation ? "Observation updated" : "Note updated");
      return;
    } else {
      // POST path. Video notes carry the match/slot anchor the
      // coach picked + the `timestamp_seconds` the player is at.
      //
      // M4 parity-audit P0-2 FIX: this used to be hardcoded `0` —
      // M4 had no player to scrub against — so every M4 video note
      // was silently pinned to 0:00. M5's `<HlsVideo>` exposes the
      // playhead via `useVideoPlayhead`; the note now saves at the
      // coach's actual position. `playhead.currentTime` is `null`
      // until the video loads — if the coach saves that early we
      // fall back to `0` (a valid `ge=0` timestamp; the note lands
      // at the slot start, same as a deliberate scrub-to-start).
      //
      // Observation notes pass null match/slot/timestamp + the
      // event fields (generated-required-with-default because
      // Pydantic gives them `Field(default="...")`).
      const body: CreateCoachingNoteRequest = {
        match_id: isObservation ? null : matchId,
        slot: isObservation ? null : slot,
        timestamp_seconds: isObservation ? null : (playhead.currentTime ?? 0),
        title: values.title,
        body: values.body,
        category: values.category,
        visibility: values.visibility,
        note_type: values.note_type,
        note_context: isObservation ? "observation" : "video",
        // Observation event metadata comes from the composer's
        // event fields (rendered only in tactical-board mode). For
        // a video note these stay empty — the backend ignores
        // event_* when `note_context === "video"`.
        event_title: isObservation ? values.event_title : "",
        event_date: isObservation ? values.event_date : "",
        event_type: isObservation ? values.event_type : "",
        player_summary: values.player_summary,
        what_happened: values.what_happened,
        why_it_matters: values.why_it_matters,
        what_to_do_next: values.what_to_do_next,
        coach_private_note: values.coach_private_note,
        player_ids: values.player_ids,
        tags: parseTagsRaw(values.tags_raw),
        // Empty string -> null so a cleared reference actually clears the
        // columns. The API rejects a half-written pair, so these travel
        // together or not at all.
        ref_video_provider: values.ref_video_provider || null,
        ref_video_id: values.ref_video_id || null,
        ref_video_label: values.ref_video_label || null,
        ref_video_title: values.ref_video_title || null,
        ref_video_thumb: values.ref_video_thumb || null,
        ...canvasFields,
      };
      try {
        await saveNote.mutateAsync({ mode: "create", body });
      } catch (err) {
        toast.error(
          isObservation ? "Couldn't save observation" : "Couldn't save note",
          { description: errorMessage(err) },
        );
        return;
      }
      toast.success(isObservation ? "Observation saved" : "Note saved");
    }

    // A1: the note saved — drop its persisted draft (keyed to
    // `edit:<id>` or `create`) so a later mount starts clean.
    clearDraft(draftScopeFor("note"));

    // M7.5 B8: per-moment sticky fields. Legacy
    // (`js/coaching/review.js:2566-2595`) clears only the
    // per-moment subset on save and keeps `category` /
    // `visibility` / `note_type` / `player_ids` sticky so a
    // coach reviewing one player can save several notes in a row
    // without re-tagging. Mirror that here: read the live form,
    // reset to DEFAULTS but with the sticky fields preserved.
    setComposerOverride(undefined);
    const live = composerRef.current?.getValues();
    if (live) {
      composerRef.current?.reset({
        // Sticky: keep these across the save.
        note_type: live.note_type,
        category: live.category,
        visibility: live.visibility,
        player_ids: live.player_ids,
        // Per-moment: everything else falls back to DEFAULTS via the
        // composer's `reset({ ...DEFAULTS, ...overrides })` merge.
      });
    } else {
      // No live values (composer not mounted) — fall back to a
      // full remount.
      setComposerKey((k) => k + 1);
    }

    // Reset the canvas state on the active source. Without this,
    // the next note saved from the same source silently inherits
    // the just-saved tactical board / drawing — stale evidence
    // attached to an unrelated note. We only clear the active
    // source so a coach mid-flow on the OTHER surface keeps their
    // in-progress work (a tactical sketch should survive a quick
    // video-note save, and vice versa).
    if (source === "tactical_board") {
      setScene(emptyScene());
    } else {
      setDrawing(emptyDrawing());
      setActiveNoteId(null);
      setActiveNoteMoment(null);
    }
  };

  // ---- Clip: source-note derivation ----
  // A clip's `source_note_id` is the note whose drawing snapshot the
  // clip should inherit. Ports legacy `_deriveClipSourceNoteId`:
  //   1. prefer the timeline chip the coach last clicked
  //      (`activeNoteId`) — but only if it's a VIDEO note on the
  //      same match/slot as the clip window;
  //   2. else fall back to a co-located note — a video note on the
  //      same match/slot whose `timestamp_seconds` falls inside the
  //      `[start, end]` window, the one closest to the window
  //      midpoint when several match.
  // CREATE-only — never re-derived on edit (a clip's source is
  // immutable; `UpdateCoachingClipRequest` doesn't carry the field).
  const deriveClipSourceNoteId = useCallback(
    (start: number, end: number): number | null => {
      const candidates = railNotes.filter(
        (n) =>
          n.note_context === "video" &&
          n.match_id === matchId &&
          n.slot === slot,
      );
      if (candidates.length === 0) return null;
      // (1) the active chip, if it qualifies + is in-window.
      if (activeNoteId != null) {
        const active = candidates.find((n) => n.id === activeNoteId);
        if (
          active &&
          Number(active.timestamp_seconds ?? -1) >= start &&
          Number(active.timestamp_seconds ?? -1) <= end
        ) {
          return active.id;
        }
      }
      // (2) co-located note closest to the window midpoint.
      const mid = (start + end) / 2;
      const inWindow = candidates
        .filter((n) => {
          const t = Number(n.timestamp_seconds ?? -1);
          return t >= start && t <= end;
        })
        .sort(
          (a, b) =>
            Math.abs(Number(a.timestamp_seconds ?? 0) - mid) -
            Math.abs(Number(b.timestamp_seconds ?? 0) - mid),
        );
      return inWindow[0]?.id ?? null;
    },
    [railNotes, matchId, slot, activeNoteId],
  );

  // ---- Clip save handler ----
  const handleClipSubmit: SubmitHandler<ClipComposerValues> = async (
    values,
  ) => {
    if (!isCurrentDraftContext(draftContext)) return;
    // A clip always needs a match anchor. Block the save with the
    // same inline error the note path uses rather than 422-ing.
    if (!matchId) {
      setMatchError("Pick a match before saving a clip.");
      return;
    }
    setMatchError(null);

    if (search.clip_id) {
      // PATCH path. `UpdateCoachingClipRequest` is `extra="forbid"`
      // and OMITS match_id/slot — a clip's anchor is immutable.
      // `source_note_id` is CREATE-only and not in the Update type.
      const body: UpdateCoachingClipRequest = {
        title: values.title,
        description: values.description,
        category: values.category,
        visibility: values.visibility,
        start_seconds: values.start_seconds,
        end_seconds: values.end_seconds,
        player_ids: values.player_ids,
        drawing: saveDrawing(drawing) as unknown as Record<string, unknown>,
      };
      try {
        await saveClip.mutateAsync({
          mode: "update",
          id: search.clip_id,
          body,
        });
      } catch (err) {
        toast.error("Couldn't save clip", { description: errorMessage(err) });
        return;
      }
      toast.success("Clip updated");
    } else {
      // POST path. The clip carries the match/slot anchor + the
      // window + the derived source note + the canvas drawing
      // snapshot. `source_note_id` is sent only on create.
      const sourceNoteId = deriveClipSourceNoteId(
        values.start_seconds,
        values.end_seconds,
      );
      const body: CreateCoachingClipRequest = {
        match_id: matchId,
        slot,
        start_seconds: values.start_seconds,
        end_seconds: values.end_seconds,
        title: values.title,
        description: values.description,
        category: values.category,
        visibility: values.visibility,
        player_ids: values.player_ids,
        ...(sourceNoteId != null ? { source_note_id: sourceNoteId } : {}),
        // Serialize to the backend's on-disk shape (`type`-keyed
        // objects) — see the note-save comment above.
        drawing: saveDrawing(drawing) as unknown as Record<string, unknown>,
      };
      try {
        await saveClip.mutateAsync({ mode: "create", body });
      } catch (err) {
        toast.error("Couldn't save clip", { description: errorMessage(err) });
        return;
      }
      toast.success("Clip saved");
    }

    // A1: the clip saved — drop its persisted draft (keyed to
    // `edit:<id>` or `create`). Runs for BOTH paths, before the
    // edit-mode early return below.
    clearDraft(draftScopeFor("clip"));

    if (search.clip_id) return;

    // Reset create-mode clip authoring so the coach can immediately
    // mark the next clip from the current playhead.
    //   - drop the focus-mode snapshot override so it doesn't
    //     re-seed the next clip with the just-saved values,
    //   - key-bump to remount the composer at fresh defaults,
    //   - clear the canvas drawing,
    //   - reseed the IN/OUT window around the current time,
    //   - close the drawer but stay in clip mode so the range tools
    //     remain available.
    setClipComposerOverride(undefined);
    setClipComposerKey((k) => k + 1);
    setDrawing(emptyDrawing());
    setActiveNoteId(null);
    setActiveNoteMoment(null);
    resetClipWindowToPlayhead();
    setComposing(false);
  };

  // ---- Render ----
  const players = bundle.data?.players ?? [];
  // Composer + template selector live INSIDE the canvas's right
  // rail via `toolbarChildren`. The canvas's built-in tool grid /
  // color / stroke / formation block sit above; the composer
  // continues the rail downward. Matches the legacy Replay coach-
  // review layout (single right column = tools + composer; left
  // column = video / pitch).
  // Submit label keys off the active SOURCE (video → note,
  // tactical_board → observation), not off whether a match is
  // loaded. Edit mode shows "Update …" for whichever kind. Note-
  // specific — the clip composer owns its own button label.
  const submitLabel = saveNote.isError
    ? "Retry save"
    : source === "tactical_board"
      ? isNoteEdit
        ? "Update observation"
        : "Save observation"
      : isNoteEdit
        ? "Update note"
        : "Save note";

  const editDetailsLabel = isClipEdit
    ? "Edit clip details"
    : source === "tactical_board"
      ? "Edit observation details"
      : "Edit note details";
  const startFreshEditAction = isClipEdit
    ? {
        label: "New clip",
        icon: <PlusIcon size={14} strokeWidth={2.5} />,
        onClick: startNewClipFromEdit,
        testId: "coach-review-new-clip-from-edit",
      }
    : source === "tactical_board"
      ? {
          label: "New observation",
          icon: <PlusIcon size={14} strokeWidth={2.5} />,
          onClick: startNewObservationFromEdit,
          testId: "coach-review-new-observation-from-edit",
        }
      : {
          label: "New note",
          icon: <PlusIcon size={14} strokeWidth={2.5} />,
          onClick: startNewNoteFromEdit,
          testId: "coach-review-new-note-from-edit",
        };
  const renderEditActions = () =>
    (isNoteEdit || isClipEdit) && !composing ? (
      <>
        <button
          ref={composeButtonRef}
          type="button"
          onClick={isClipEdit ? openComposerClip : openComposerNote}
          data-testid="coach-review-edit-details"
          className="inline-flex min-h-10 flex-1 items-center justify-center gap-1.5 rounded bg-accent px-3 py-1.5 font-display text-[13px] font-semibold text-accent-ink transition-colors hover:bg-accent-hover sm:min-h-0 sm:flex-none sm:justify-start"
        >
          {isClipEdit ? <FilmIcon size={14} /> : <PencilIcon size={14} />}
          {editDetailsLabel}
        </button>
        <button
          type="button"
          onClick={startFreshEditAction.onClick}
          data-testid={startFreshEditAction.testId}
          className="editor-edit-action-secondary inline-flex min-h-10 flex-1 items-center justify-center gap-1.5 rounded border bg-transparent px-3 py-1.5 font-display text-[13px] font-semibold transition-colors sm:min-h-0 sm:flex-none sm:justify-start"
        >
          {startFreshEditAction.icon}
          {startFreshEditAction.label}
        </button>
      </>
    ) : null;

  // ---- v2 tool-rail relocation ----
  // The canvas's tool grid / color / stroke controls are relocated
  // out of the canvas's own column into the v2 right rail via the
  // canvas's `toolRailSlot` render-prop. The canvas hands us the
  // control block; we portal it into this rail target so it lands
  // in the rail's "Tools" section. State (not just a ref) so the
  // first portal render happens once the rail node mounts.
  const [toolRailTarget, setToolRailTarget] = useState<HTMLElement | null>(
    null,
  );
  const [drawingToolsOpen, setDrawingToolsOpen] = useState(() =>
    typeof window === "undefined" || typeof window.matchMedia !== "function"
      ? true
      : window.matchMedia("(min-width: 1024px)").matches,
  );
  const [formationControlsTarget, setFormationControlsTarget] =
    useState<HTMLElement | null>(null);

  // ---- v2 clip track block ----
  // Clip mode is active whenever the clip composer is the one in
  // play (create or edit) on the video surface. The clip track,
  // preset bar, and thumbnail strip render only then.
  const inClipMode = clipPlaybackActive;
  // Clips on this match — the clip rail + clip-track lane data.
  // Primary source is the per-match query (`useCoachClips` →
  // `GET /api/coach/clips?match_id=`). The bundle's edit-mode seed
  // clip is unioned in so an edit target shows immediately on
  // edit-load (before the query settles) and so a clip not yet in
  // the freshest list page still renders; deduped by id, seed last
  // so the list copy wins once it lands.
  const matchClips = useMemo(() => {
    const fetched = coachClipsQuery.data?.clips ?? [];
    const seed =
      bundle.data?.clip && bundle.data.clip.match_id === matchId
        ? bundle.data.clip
        : null;
    if (!seed || fetched.some((c) => c.id === seed.id)) return fetched;
    return [...fetched, seed];
  }, [coachClipsQuery.data?.clips, bundle.data?.clip, matchId]);

  // A4: players to suggest as quick-add chips — frequency-ranked from
  // this match's existing notes + clips, filtered to the roster (no
  // ghosts). The composer filters out anyone already tagged live, so
  // we pass an empty `alreadyTagged` here.
  const suggestedPlayers = useMemo(
    () =>
      suggestedPlayerIds({
        notes: railNotes,
        clips: matchClips,
        rosterPlayerIds: players.map((p) => p.id),
        alreadyTagged: [],
      }),
    [railNotes, matchClips, players],
  );
  // The approved transport + timeline + clip controls, rendered in
  // one compact dock below the real video stage.
  const editorTime = playhead.currentTime ?? 0;
  const activeClipDuration = clipWindow
    ? Math.max(0, clipWindow.end - clipWindow.start)
    : 0;
  const clipTrackBlock = (
    <section
      className={["review-editor", inClipMode ? "is-clipping" : ""]
        .filter(Boolean)
        .join(" ")}
      data-testid="coach-review-clip-block"
      data-focus={focusOpen ? "true" : "false"}
      aria-label="Match playback and feedback editor"
    >
      <div className="editor-top-row">
        <div className="editor-playback">
          <FrameStepper videoRef={videoElRef} />
          <div className="editor-time" data-testid="review-editor-time">
            <strong>{formatEditorTime(editorTime)}</strong>
            <span>/ {formatPlayhead(duration || null)}</span>
          </div>
          <button
            type="button"
            className="editor-button editor-rate"
            onClick={cyclePlaybackRate}
            aria-label="Change playback speed"
            title="Change playback speed"
          >
            {playbackRate}×
          </button>
        </div>

        <div className="editor-authoring">
          {!isNoteEdit && !isClipEdit && !composing ? (
            <>
              <div className="editor-mark-group">
                <button
                  ref={inClipMode ? undefined : composeButtonRef}
                  type="button"
                  onClick={openComposerNote}
                  data-testid="coach-review-mark-moment"
                  className="editor-button editor-mark bg-accent"
                >
                  <MessageSquarePlus size={16} aria-hidden />
                  <span>
                    Mark moment{" "}
                    <span data-mark-time>{formatClock(editorTime)}</span>
                  </span>
                  <kbd>M</kbd>
                </button>
                <span className="editor-mark-description">
                  Feedback on this frame
                </span>
              </div>
              {!inClipMode ? (
                <button
                  type="button"
                  onClick={enterClipMode}
                  data-testid="coach-review-make-clip"
                  className="editor-button editor-create bg-transparent"
                >
                  <Scissors size={16} aria-hidden />
                  <span>Create clip</span>
                  <kbd>C</kbd>
                </button>
              ) : null}
            </>
          ) : null}
          {(isNoteEdit || isClipEdit) && !composing
            ? renderEditActions()
            : null}
        </div>
      </div>

      <ClipTrack
        duration={duration}
        playhead={editorTime}
        onSeek={seekVideo}
        clipWindow={clipWindow}
        onClipWindowChange={applyClipWindow}
        inClipMode={inClipMode}
        notes={railNotes}
        clips={matchClips}
        activeNoteId={activeNoteId}
        onJumpToNote={handleRailNoteClick}
        matchName={
          selectedMatch ? matchLabel(selectedMatch) : "Match recording"
        }
        slotName={SLOT_LABELS[slot as MatchSlot] ?? slot}
        matchId={selectedMatch?.id}
        slot={slot}
      />

      {inClipMode && clipWindow ? (
        <div className="editor-clip-row">
          <div className="editor-clip-range" data-testid="clip-track-readout">
            <span>
              <b>IN</b> {formatEditorTime(clipWindow.start)}
            </span>
            <span>
              <b>OUT</b> {formatEditorTime(clipWindow.end)}
            </span>
            <span className="editor-duration">
              <strong data-testid="coach-review-clip-duration">
                {formatDuration(activeClipDuration)}
              </strong>{" "}
              selected
            </span>
          </div>
          <div className="editor-clip-actions">
            {duration > 0 ? (
              <ClipPresetBar
                duration={duration}
                window={clipWindow}
                onSetWindow={applyClipWindow}
                onSetIn={setClipIn}
                onSetOut={setClipOut}
              />
            ) : (
              <span className="editor-waiting">
                Set IN / OUT after the video is ready.
              </span>
            )}
            {!isNoteEdit && !isClipEdit && !composing ? (
              <>
                <button
                  ref={composeButtonRef}
                  type="button"
                  onClick={openComposerClip}
                  data-testid="coach-review-add-clip-details"
                  className="editor-button editor-add-details"
                >
                  <NotebookPen size={16} aria-hidden />
                  Add details
                </button>
                <button
                  type="button"
                  onClick={exitClipMode}
                  data-testid="coach-review-exit-clip"
                  className="editor-button"
                >
                  Exit clip
                </button>
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="editor-status">
        <span>
          {duration > 0 ? "Recording ready" : "Waiting for recording"}
        </span>
        <span>
          {inClipMode
            ? `Drag IN / OUT to trim · Play loops IN to OUT · ${MAX_CLIP_DURATION_SECONDS}s maximum`
            : "Select a saved marker to revisit its frame"}
        </span>
      </div>
    </section>
  );

  // Timecode line for the composer drawer's header — the clip
  // `IN → OUT · DUR` range in clip mode, otherwise the playhead the
  // note will save at.
  const composerTimecodeLabel =
    composerMode === "clip" && clipWindow
      ? `${formatPlayhead(clipWindow.start)} → ${formatPlayhead(
          clipWindow.end,
        )}`
      : source === "tactical_board"
        ? "Observation"
        : playhead.formatted;
  const composerContextLabel =
    source === "tactical_board"
      ? "Tactical board"
      : selectedMatch
        ? matchLabel(selectedMatch)
        : "No match selected";

  // The canvas's `toolRailSlot` implementation — portals the
  // canvas's own tool controls into the v2 right rail's Tools
  // section. Returns a portal (which renders into `toolRailTarget`),
  // so nothing shows at the canvas's location. Until the rail node
  // has mounted (`toolRailTarget` null on the very first paint) it
  // returns null and the controls simply aren't shown for that one
  // frame — the rail mounts in the same commit, so the next render
  // fills it.
  const renderToolRailSlot = (toolControls: React.ReactNode) =>
    toolRailTarget
      ? createPortal(<ToolRail>{toolControls}</ToolRail>, toolRailTarget)
      : null;
  const renderFormationControlsSlot = (formationControls: React.ReactNode) =>
    formationControlsTarget
      ? createPortal(formationControls, formationControlsTarget)
      : null;

  // ---- v2 top-row context controls ----
  // Video mode anchors notes/clips to a match + slot. The picker is
  // now rendered in the top control row beside the source toggle.
  // Tactical-board formation controls are portaled into that same
  // row by `TacticalBoardEditor` via `formationControlsSlot`.
  const railHeader =
    source === "video" ? (
      <div
        className="flex items-center gap-2"
        data-testid="coach-review-rail-header"
      >
        <MatchSlotPicker
          matches={matchesQuery.data ?? []}
          matchesLoading={matchesQuery.isLoading}
          matchId={matchId}
          onMatchIdChange={(id) => {
            if (id !== matchId) prepareClipRecordingChange();
            setMatchId(id);
            if (id) setMatchError(null);
            // The admin-kill banner is cleared by the
            // `[matchId, slot]` effect — one source of truth.
          }}
          slot={slot}
          onSlotChange={(nextSlot) => {
            if (nextSlot !== slot) prepareClipRecordingChange();
            setSlot(nextSlot);
          }}
          disabled={isClipEdit}
        />
        {/* Live timestamp readout — a new video note saves at the
         * player's current position. `--:--` until the video loads.
         * Note-create only. */}
        {!isNoteEdit && !isClipEdit ? (
          <p
            className="shrink-0 whitespace-nowrap text-ink-muted text-xs"
            data-testid="coach-review-timestamp-readout"
          >
            Note saves at{" "}
            <span className="font-mono text-ink">{playhead.formatted}</span>
          </p>
        ) : null}
      </div>
    ) : null;

  const composerElement = (
    <div className="flex flex-col gap-4">
      {/* The match + slot picker, the live timestamp readout, and
       * the canvas-control rail all moved OUT of this drawer into
       * the v2 right rail (see `railHeader` below). The Save Note /
       * Save Clip toggle was removed entirely — the drawer's kind is
       * fixed by which trigger opened it ("Mark moment" → note,
       * "Make clip" → clip). The drawer now holds only the feedback
       * form. `matchError` is a save-time validation message, so it
       * stays here next to the composer. */}
      {source === "video" && matchError ? (
        <p
          className="text-destructive font-mono text-xs"
          data-testid="coach-review-match-error"
        >
          {matchError}
        </p>
      ) : null}
      {composerMode === "clip" && source === "video" ? (
        <>
          <ClipComposer
            ref={clipComposerRef}
            key={
              isClipEdit
                ? `edit-${bundle.data?.clip?.id ?? "loading"}-${clipComposerKey}`
                : `create-${clipComposerKey}`
            }
            defaultValues={
              // Precedence: a focus-mode snapshot wins (it carries
              // the coach's live edits across the portal remount);
              // then the edit-mode seed clip; then the frozen
              // create-mode window. The v2 clip track's live
              // `clipWindow` overrides start/end on top — the track
              // is the source of truth for the window, and the
              // composer drawer mounts fresh (key-bumped) each open
              // so a track drag never churns the composer's other
              // fields mid-edit.
              {
                ...(clipComposerOverride ??
                  clipEditDefaults ??
                  clipCreateDefaults ??
                  {}),
                ...(clipWindow
                  ? {
                      start_seconds: Number(clipWindow.start.toFixed(1)),
                      end_seconds: Number(clipWindow.end.toFixed(1)),
                    }
                  : {}),
              }
            }
            players={players}
            onSubmit={handleClipSubmit}
            submitLabel={
              saveClip.isError
                ? "Retry save"
                : isClipEdit
                  ? "Update clip"
                  : "Save clip"
            }
            matchLabel={
              selectedMatch ? matchLabel(selectedMatch) : "No match selected"
            }
            slot={slot}
            isEditing={isClipEdit}
            sourceNoteId={bundle.data?.clip?.source_note_id ?? null}
            // A4: quick-add chips from this match's tagging history.
            suggestedPlayerIds={suggestedPlayers}
            onCancel={closeComposer}
          />
          {/* Preview clip — opens the focused feedback player modal.
           * Only in clip-EDIT mode: the bundle's seed clip is a
           * persisted, fully-shaped clip to preview. (Create mode
           * has no saved clip yet — the coach saves first, then
           * the route flips to ?clip_id= and the button appears.) */}
          {isClipEdit && bundle.data?.clip && selectedMatch ? (
            <button
              type="button"
              onClick={() => setClipPreviewOpen(true)}
              data-testid="coach-review-preview-clip"
              className="rounded border border-rule bg-surface px-4 py-2 text-sm font-display uppercase tracking-wide text-ink hover:bg-field transition-colors"
            >
              Preview clip
            </button>
          ) : null}
        </>
      ) : (
        <>
          <Composer
            ref={composerRef}
            key={`${bundle.data?.note?.id ?? "create"}-${composerKey}`}
            defaultValues={composerOverride ?? composerDefaults}
            players={players}
            onSubmit={handleSubmit}
            submitLabel={submitLabel}
            observationMode={source === "tactical_board"}
            // A4: quick-add chips from this match's tagging history.
            suggestedPlayerIds={suggestedPlayers}
            onCancel={closeComposer}
            templateSelector={
              <TemplateSelector
                getCurrentValues={getLiveComposerValues}
                onApply={handleTemplateApply}
              />
            }
          />
          {/* Preview note — opens the focused feedback player modal
           * in note mode (video + telestration replay). Only for a
           * VIDEO note being edited: the bundle's seed note is a
           * persisted, fully-shaped note with a match anchor.
           * Observations have no video to play. */}
          {isNoteEdit &&
          bundle.data?.note?.note_context === "video" &&
          selectedMatch ? (
            <button
              type="button"
              onClick={() => setNotePreviewOpen(true)}
              data-testid="coach-review-preview-note"
              className="rounded border border-rule bg-surface px-4 py-2 text-sm font-display uppercase tracking-wide text-ink hover:bg-field transition-colors"
            >
              Preview note
            </button>
          ) : null}
        </>
      )}
    </div>
  );

  const workspaceHeader = (
    <WorkspaceHeader
      active="review"
      visibleTabs={
        me.data?.user && !canAuthorCoachFeedback(me.data)
          ? OPERATIONAL_COACH_TABS
          : undefined
      }
    />
  );

  if (!draftContext) {
    return (
      <main className="coach-review-page min-h-screen p-4 text-ink sm:p-6">
        {workspaceHeader}
        <CoachReviewAuthGate bundle={bundle} me={me} />
        {selectionRequired ? <TeamScopeGate /> : null}
      </main>
    );
  }

  return (
    <main className="coach-review-page min-h-screen p-4 text-ink sm:p-6">
      {workspaceHeader}

      {/* M9a A8 — auth gate. The shell-level <CoachReviewAuthGate>
       * effect (below) handles the redirect to `/login?next=/coach/review`.
       * Nothing renders in place of the old <InlineLoginPanel> — the
       * redirect happens before the user sees a flash of empty UI. */}
      <CoachReviewAuthGate bundle={bundle} me={me} />

      {bundle.isError &&
      !(bundle.error instanceof ApiError && bundle.error.status === 401) ? (
        <p
          className="text-destructive font-mono text-sm mb-4"
          data-testid="coach-review-bundle-error"
        >
          Failed to load bundle:{" "}
          {bundle.error instanceof Error ? bundle.error.message : "unknown"}
        </p>
      ) : null}
      {saveNote.isError ? (
        <p
          className="text-destructive font-mono text-sm mb-4"
          data-testid="coach-review-save-error"
        >
          Save failed:{" "}
          {saveNote.error instanceof Error ? saveNote.error.message : "unknown"}
        </p>
      ) : null}
      {streamKilled ? (
        <p
          className="text-destructive font-mono text-sm mb-4"
          data-testid="coach-review-stream-killed-error"
        >
          This stream was disconnected by an administrator. Pick a different
          match to resume.
        </p>
      ) : null}

      <div
        className="flex flex-col gap-4"
        data-testid="coach-review-shell"
        hidden={
          bundle.isError &&
          bundle.error instanceof ApiError &&
          bundle.error.status === 401
            ? true
            : undefined
        }
      >
        {/* Stage-control row — source toggle, source-specific
         * context controls, then surface actions. Match/slot and
         * tactical formation controls live here so they stay visible
         * even when the drawing tools are tucked into the mobile
         * slide-in panel. */}
        <div className="review-stage-controls flex items-center gap-3 overflow-x-auto pb-1">
          <div className="flex min-w-max flex-1 items-center gap-3">
            <SourceToggle value={source} onChange={switchSource} />
            <div
              className="shrink-0"
              data-testid="coach-review-context-controls"
            >
              {source === "video" ? (
                railHeader
              ) : (
                <div ref={setFormationControlsTarget} />
              )}
            </div>
          </div>
          <div
            className="review-stage-actions relative flex shrink-0 items-center gap-2"
            data-testid="coach-review-stage-actions"
          >
            <button
              type="button"
              onClick={() => setDrawingToolsOpen((open) => !open)}
              aria-expanded={drawingToolsOpen}
              aria-controls="coach-review-drawing-tools"
              data-testid="coach-review-tools-button"
              className="inline-flex h-9 items-center rounded border border-rule bg-surface px-3 font-display text-sm uppercase tracking-wide text-ink transition-colors hover:bg-field"
            >
              <span className="review-tools-label-desktop">Tools</span>
              <span className="review-tools-label-mobile">Drawing tools</span>
            </button>
            {focusModeEnabled ? (
              <button
                ref={focusButtonRef}
                type="button"
                onClick={openFocus}
                data-testid="coach-review-focus-button"
                className="rounded border border-rule bg-surface px-3 py-1.5 font-display text-sm uppercase tracking-wide text-ink transition-colors hover:bg-field"
              >
                <Maximize size={18} aria-hidden /> Focus mode
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setHelpOpen((v) => !v)}
              aria-expanded={helpOpen}
              aria-label="Keyboard shortcuts"
              data-testid="coach-review-help-button"
              className="h-9 w-9 rounded border border-rule bg-surface font-mono text-ink-muted transition-colors hover:bg-field hover:text-ink"
            >
              <Keyboard size={18} aria-hidden />
            </button>
          </div>
        </div>

        {/* v2 two-column workspace — the stage column (flex-1) and
         * the notes/clips rail (320px). The canvas's own tool /
         * composer rail still lives inside the canvas via
         * `toolbarChildren`; this NotesAndClipsRail is the v2
         * addition that splits the note + clip lists into their own
         * consistently-styled column. */}
        <div className="review-stage-group flex flex-col gap-4">
          {!focusOpen && drawingToolsOpen ? (
            <>
              <button
                type="button"
                className="review-tools-backdrop"
                aria-label="Close drawing tools"
                onClick={() => setDrawingToolsOpen(false)}
              />
              <aside
                id="coach-review-drawing-tools"
                aria-label="Drawing tools"
                className="review-drawing-toolbar"
              >
                <div className="review-tools-sheet-header">
                  <div>
                    <span>Drawing tools</span>
                    <small>Choose a tool, then draw on the stage.</small>
                  </div>
                  <button
                    type="button"
                    aria-label="Close drawing tools"
                    onClick={() => setDrawingToolsOpen(false)}
                  >
                    <X size={20} aria-hidden="true" />
                  </button>
                </div>
                <div ref={setToolRailTarget} />
              </aside>
            </>
          ) : null}
          <section
            ref={stageMeasureRef}
            className="min-w-0 flex-1 overflow-hidden"
            aria-label="Stage"
            id="coach-review-source-panel"
            role="tabpanel"
            aria-labelledby={`coach-review-source-tab-${source}`}
          >
            {/* When focus mode is open, the inline stage is hidden —
             * the same canvas is mounted inside the fullscreen
             * overlay portal below. Two stage mounts would create
             * conflicting setSurface effects on the review-editor
             * store. The composer node is also rendered (via the
             * canvas's `toolbarChildren` slot below the tool grid),
             * so it stays alive inside the inline canvas — RHF state
             * survives focus-mode toggles. */}
            {!focusOpen ? (
              source === "tactical_board" ? (
                <div className="flex flex-col gap-3">
                  <TacticalBoardEditor
                    appearance="navy"
                    scene={scene}
                    onSceneChange={setScene}
                    width={boardStageWidth}
                    players={players}
                    toolRailSlot={renderToolRailSlot}
                    formationControlsSlot={renderFormationControlsSlot}
                  />
                  {/* Observation details are explicit in edit mode so
                   * the board scene remains the first thing the coach
                   * sees. New observations still use Mark observation. */}
                  {isNoteEdit && !composing ? (
                    <div className="flex flex-col gap-2">
                      <p
                        className="font-mono text-[11px] text-ink-muted"
                        data-testid="coach-review-observation-edit-hint"
                      >
                        The board shows this saved observation — edits update
                        it. New observation clears the board for a fresh one.
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        {renderEditActions()}
                      </div>
                    </div>
                  ) : !isNoteEdit && !isClipEdit && !composing ? (
                    <div className="flex">
                      {/* A4: Mark observation is the sole/primary
                       * creation action on the tactical board — filled
                       * accent, mirroring "Mark moment" in video mode. */}
                      <button
                        ref={composeButtonRef}
                        type="button"
                        onClick={openComposerNote}
                        data-testid="coach-review-mark-observation"
                        className="inline-flex min-h-10 flex-1 items-center justify-center gap-1.5 rounded bg-accent px-3 py-1.5 font-display text-[13px] font-semibold text-accent-ink transition-colors hover:bg-accent-hover sm:min-h-0 sm:flex-none sm:justify-start"
                      >
                        <PlusIcon size={14} strokeWidth={2.5} />
                        Mark observation
                        <kbd className="rounded bg-accent-ink/20 px-1 py-px font-mono text-[9px]">
                          M
                        </kbd>
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <TelestratorCanvas
                    drawing={drawing}
                    onDrawingChange={setDrawing}
                    width={videoStageWidth}
                    match={selectedMatch}
                    slot={slot}
                    videoRef={handleVideoElRef}
                    onHeartbeatKilled={() => setStreamKilled(true)}
                    toolRailSlot={renderToolRailSlot}
                    showDrawing={showVideoDrawing}
                  />
                  {/* v2 clip-track block — transport + scrub track +
                   * (clip mode) preset bar + thumbnail strip. Replaces
                   * the v1 TimelineChipRail: the note marks now live on
                   * the scrub track itself, and the right rail's
                   * NotesAndClipsRail carries the note list. */}
                  {clipTrackBlock}
                </div>
              )
            ) : (
              <div
                className="rounded border border-rule bg-surface text-ink-muted text-sm font-mono px-4 py-3"
                data-testid="coach-review-stage-focused-placeholder"
              >
                Stage is open in focus mode. Press <kbd>Esc</kbd> or click Exit
                to return.
              </div>
            )}
          </section>
        </div>

        {/* Context rail — video shows match-specific notes/clips;
         * tactical-board mode shows recent team observations instead
         * of a misleading match rail. */}
        {!focusOpen ? (
          source === "tactical_board" ? (
            <RecentObservationsRail
              observations={recentObservations}
              players={players}
              loading={bundle.isLoading}
              activeObservationId={isNoteEdit ? search.note_id : null}
              // Route through the same switch logic as the notes rail. A bare
              // navigate here left `composerOverride` — the snapshot
              // `closeComposer` takes of the note being left — in place, and
              // that override outranks the defaults the bundle loads from
              // `?note_id=`, so the editor kept replaying the previous
              // observation. Going through `requestNoteSwitch` also gets the
              // unsaved-edits prompt, which this path never had.
              onOpenObservation={(note) => {
                if (isNoteEdit) {
                  requestNoteSwitch(note);
                  return;
                }
                void navigate({
                  to: "/coach/review",
                  search: { note_id: note.id, source: "tactical_board" },
                });
              }}
            />
          ) : selectedMatch ? (
            <NotesAndClipsRail
              notes={railNotes}
              players={players}
              activeClipId={search.clip_id ?? null}
              clips={matchClips}
              activeNoteId={activeNoteId}
              onJumpToNote={handleRailNoteClick}
              onOpenClip={openClipForEdit}
              notesLoading={coachNotesQuery.isLoading}
              clipsLoading={coachClipsQuery.isLoading}
              covered={composing}
            />
          ) : (
            <section
              className="rounded border border-rule bg-surface p-4"
              data-testid="coach-review-video-context-empty"
            >
              <h4 className="mb-2 font-mono text-[10px] uppercase tracking-mono text-ink-muted">
                Match notes and clips
              </h4>
              <p className="flex h-[4.5rem] w-full items-center rounded border border-dashed border-rule px-3 font-mono text-xs text-ink-muted">
                Select a match to see its notes and clips.
              </p>
            </section>
          )
        ) : null}
      </div>

      {/* Composer drawer — the v2 bottom slide-up that hosts the
       * note / clip composer. Mounted only while `composing` is true
       * AND focus mode is closed (focus mode hosts the composer in
       * its own floating panel — see the FocusModeOverlay below — so
       * mounting it here too would double-mount the form). */}
      {composing && !focusOpen ? (
        <ComposerDrawer
          kind={composerMode}
          observation={source === "tactical_board"}
          editing={isNoteEdit || isClipEdit}
          timecodeLabel={composerTimecodeLabel}
          contextLabel={composerContextLabel}
          onClose={closeComposer}
          // #366 — the drawer overlays the page's source tabs, but
          // flipping Video ↔ Tactical board mid-compose is a designed
          // flow (the composer relabels off the source). A compact
          // toggle in the drawer header keeps it mouse-reachable.
          // Distinct testids/attributes from the page SourceToggle so
          // strict-mode locators never see duplicates; the body
          // attribute mirror stays owned by the page toggle alone.
          headerSlot={
            <div className="review-composer-header-tools">
              <div
                role="tablist"
                aria-label="Composer source"
                data-testid="drawer-source-toggle"
                className="review-composer-source-toggle inline-flex rounded border border-rule bg-canvas p-0.5"
              >
                {(
                  [
                    ["video", "Video"],
                    ["tactical_board", "Board"],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={source === value}
                    data-drawer-source={value}
                    onClick={() => switchSource(value)}
                    className={[
                      "rounded px-2 py-0.5 font-display text-[11px] uppercase tracking-wide transition-colors",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent",
                      source === value
                        ? "bg-accent text-accent-ink"
                        : "text-ink-muted hover:bg-field hover:text-ink",
                    ].join(" ")}
                  >
                    {value === "video" ? (
                      <Video
                        className="review-composer-source-icon"
                        aria-hidden
                      />
                    ) : (
                      <PanelsTopLeft
                        className="review-composer-source-icon"
                        aria-hidden
                      />
                    )}
                    <span className="review-composer-source-label">
                      {label}
                    </span>
                  </button>
                ))}
              </div>
              {focusModeEnabled ? (
                <button
                  type="button"
                  onClick={openFocus}
                  data-testid="coach-review-drawer-focus-button"
                  className="review-composer-focus"
                >
                  <Maximize size={15} aria-hidden />
                  <span>Focus</span>
                </button>
              ) : null}
            </div>
          }
          contextSlot={
            isNoteEdit && railNotes.length > 1 ? (
              <TkticSelect
                ariaLabel="Editing note"
                data-testid="drawer-note-switcher"
                size="sm"
                value={String(search.note_id ?? "")}
                onChange={(next) => {
                  const target = railNotes.find((n) => String(n.id) === next);
                  if (target) requestNoteSwitch(target);
                }}
                options={railNotes.map((n) => ({
                  value: String(n.id),
                  label: n.title?.trim() || `Note ${n.id}`,
                }))}
              />
            ) : null
          }
        >
          {composerElement}
        </ComposerDrawer>
      ) : null}

      {/* Switching notes mid-edit with unsaved changes. Three real
       * outcomes, so the dialog carries a third action: save and move on,
       * throw the edits away and move on, or stay put. Discarding is the
       * only irreversible one, so it takes the destructive styling. */}
      {pendingNoteSwitch ? (
        <ConfirmDialog
          title="Save your changes first?"
          message={`You have unsaved edits to this note. Switching to “${
            pendingNoteSwitch.title?.trim() || `Note ${pendingNoteSwitch.id}`
          }” will leave them behind.`}
          confirmLabel="Save and switch"
          cancelLabel="Stay here"
          altAction={{
            label: "Discard and switch",
            danger: true,
            "data-testid": "coach-review-switch-discard",
            onClick: () => {
              const target = pendingNoteSwitch;
              if (target) goToNote(target);
            },
          }}
          onConfirm={() => {
            const target = pendingNoteSwitch;
            const live = composerRef.current?.getValues();
            setPendingNoteSwitch(null);
            if (!target) return;
            // Save, then move on once the write lands — navigating first
            // would unmount the form mid-flight.
            void (async () => {
              if (live) await submitNoteValues(live as ComposerValues);
              goToNote(target);
            })();
          }}
          onClose={() => setPendingNoteSwitch(null)}
          data-testid="coach-review-switch-confirm"
        />
      ) : null}

      {/* Blank-wipe confirm — see the guard in handleSubmit. */}
      {pendingWipeValues ? (
        <ConfirmDialog
          title="Save with an empty board?"
          message="The board is empty, but this observation has a saved drawing. Updating now removes that drawing permanently."
          confirmLabel="Save empty board"
          danger
          onConfirm={() => {
            const values = pendingWipeValues;
            setPendingWipeValues(null);
            if (values) void submitNoteValues(values);
          }}
          onClose={() => setPendingWipeValues(null)}
          data-testid="coach-review-board-wipe-confirm"
        />
      ) : null}

      {focusOpen && focusModeEnabled ? (
        <FocusModeOverlay
          source={source}
          onSourceChange={switchSource}
          scene={scene}
          onSceneChange={setScene}
          drawing={drawing}
          onDrawingChange={setDrawing}
          match={selectedMatch}
          slot={slot}
          teamName={bundle.data?.active_scope.team_name}
          videoRef={handleVideoElRef}
          onHeartbeatKilled={() => setStreamKilled(true)}
          onClose={closeFocus}
          composer={composerElement}
          composerOpen={composing}
          onComposerOpen={openComposerNote}
          onComposerClose={closeComposer}
          playbackEditor={clipTrackBlock}
          showDrawing={showVideoDrawing}
          players={players}
        />
      ) : null}

      {/* Focused feedback player modal — M5 coach-side scope.
       * Previews the clip being edited. Renders only when the
       * bundle has a seed clip + a resolved match for its stream
       * URLs. */}
      {clipPreviewOpen && bundle.data?.clip && selectedMatch ? (
        <FeedbackPlayerModal
          target={{ kind: "clip", clip: bundle.data.clip }}
          match={selectedMatch}
          onClose={() => setClipPreviewOpen(false)}
          presentation="coach"
          matchLabel={`${selectedMatch.home_team} vs ${selectedMatch.away_team}`}
        />
      ) : null}
      {notePreviewOpen && bundle.data?.note && selectedMatch ? (
        <FeedbackPlayerModal
          target={{ kind: "note", note: bundle.data.note }}
          match={selectedMatch}
          onClose={() => setNotePreviewOpen(false)}
          presentation="coach"
          matchLabel={`${selectedMatch.home_team} vs ${selectedMatch.away_team}`}
        />
      ) : null}

      {/* M7.5 B7: source-aware shortcuts popover — swaps content
       * per active `source`. Ports the legacy
       * `_refreshCoachShortcutsHelpForSource` switch in
       * `js/coaching/review.js:798-833`. */}
      {helpOpen ? (
        <CoachModal
          title="Keyboard shortcuts"
          onClose={() => setHelpOpen(false)}
          data-testid="coach-review-help-popover"
        >
          <div data-source={source} className="flex flex-col gap-3">
            <p className="text-xs text-ink-muted">
              {source === "tactical_board" ? "Tactical board" : "Video"}
            </p>
            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
              {(source === "tactical_board"
                ? TACTICAL_SHORTCUTS
                : VIDEO_SHORTCUTS
              )
                .filter(([keys]) => focusModeEnabled || keys !== "F")
                .map(([keys, label]) => (
                  <ShortcutRow key={keys} keys={keys} label={label} />
                ))}
            </dl>
            <p className="text-xs text-ink-muted">
              Shortcuts pause while you type in a form field.
            </p>
          </div>
        </CoachModal>
      ) : null}
    </main>
  );
}

// --- M7.5 B7: source-aware keyboard-shortcut tables ------------------
// Ports the legacy `_refreshCoachShortcutsHelpForSource` switch
// (`js/coaching/review.js:798-833`). Format: `[keysHtml, label]`
// pairs; `keysHtml` is a string with one or more keys separated by
// " / " (`ShortcutRow` splits + styles each).

const VIDEO_SHORTCUTS: ReadonlyArray<[string, string]> = [
  ["Space / K", "Play / pause"],
  ["J", "Back 5s"],
  ["← / →", "Back / forward 1s"],
  ["Shift+← / →", "Back / forward 10s"],
  [", / .", "Step back / forward 1 frame"],
  ["Shift+, / .", "Step back / forward 1 second"],
  ["C / M", "Clip mode / Mark moment"],
  ["I / O", "Set clip IN / OUT"],
  ["[", "Jump to clip first frame"],
  ["F", "Toggle focus mode"],
  ["V / B", "Switch to Video / Board"],
  ["1 – 4", "Quick-pick tool"],
  ["A / Z / T", "Arrow / Zone / Label"],
  ["S / D / G", "Spotlight / Dim / Formation"],
  ["Esc", "Disarm / close"],
  ["?", "Toggle this help"],
];

const TACTICAL_SHORTCUTS: ReadonlyArray<[string, string]> = [
  ["F", "Toggle focus mode"],
  ["V / B", "Switch to Video / Board"],
  ["M", "Mark observation"],
  ["1 – 4", "Quick-pick tool"],
  ["A / Z / T", "Arrow / Zone / Label"],
  ["P / S / D / G", "Player / Spotlight / Dim / Formation"],
  ["Delete / Backspace", "Delete selected"],
  ["Esc", "Disarm + clear selection"],
  ["?", "Toggle this help"],
];

function ShortcutRow({ keys, label }: { keys: string; label: string }) {
  return (
    <>
      <dt className="text-ink-muted">{keys}</dt>
      <dd>{label}</dd>
    </>
  );
}

/**
 * Fullscreen focus-mode overlay. The active 16:9 canvas fills the
 * viewport while the approved header, translucent drawing dock and
 * source-specific bottom controls float above it.
 *
 * Portaled to `<body>` so the parent's bounded stacking context
 * (CSS `animation` on the route component creates one) can't trap
 * the overlay's z-index.
 *
 * Esc closes. The overlay tags itself `data-coach-review-portal` so
 * any future inert sweep (not currently active here — focus mode
 * doesn't need keyboard-trap because all interactable elements are
 * inside the overlay and the canvas pointer surface is the whole
 * viewport) would skip the portal container.
 */
function FocusModeOverlay(props: {
  source: CoachReviewSource;
  onSourceChange: (source: CoachReviewSource) => void;
  scene: BoardScene;
  onSceneChange: (s: BoardScene) => void;
  drawing: DrawingV2;
  onDrawingChange: (d: DrawingV2) => void;
  /** Resolved match + slot for the telestrator's `<HlsVideo>` —
   * `undefined` when no match is selected (the canvas then shows
   * the no-video placeholder). */
  match: Match | undefined;
  slot: string;
  teamName?: string;
  players: Player[];
  /** Forwarded to the telestrator — the parent's callback ref that
   * captures the backing `<video>`, so the playhead readout /
   * keyboard scrubbing / save timestamp keep working while focus
   * mode owns the canvas. */
  videoRef: (el: HTMLVideoElement | null) => void;
  /** Forwarded to the telestrator — fires when the VOD heartbeat
   * gets a 403 (admin kill). */
  onHeartbeatKilled?: () => void;
  showDrawing: boolean;
  onClose: () => void;
  /** The composer stack — rendered inside the floating tool panel
   * below the drawing tools so focus mode is a full authoring
   * surface (draw + write), not draw-only. */
  composer: React.ReactNode;
  composerOpen: boolean;
  onComposerOpen: () => void;
  onComposerClose: () => void;
  playbackEditor: React.ReactNode;
}) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [overlaySize, setOverlaySize] = useState(() => ({
    width: typeof window === "undefined" ? 1200 : window.innerWidth,
    height: typeof window === "undefined" ? 800 : window.innerHeight,
  }));

  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setOverlaySize({
          width: Math.max(1, Math.floor(entry.contentRect.width)),
          height: Math.max(200, Math.floor(entry.contentRect.height)),
        });
      }
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    document.body.classList.add("review-focus-active");
    return () => document.body.classList.remove("review-focus-active");
  }, []);

  // The product tree sits behind a body portal. Keep background navigation
  // out of the tab order and restore the initiating control on exit.
  useEffect(() => {
    const root = document.getElementById("root");
    const wasInert = root?.inert ?? false;
    const previous =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    if (root) root.inert = true;
    overlayRef.current
      ?.querySelector<HTMLButtonElement>(
        "[data-testid='coach-review-focus-exit']",
      )
      ?.focus();
    return () => {
      if (root) root.inert = wasInert;
      previous?.focus();
    };
  }, []);

  // Esc closes the overlay. Doesn't preventDefault — the broader
  // M4 keyboard-shortcut handler also reacts to Esc (disarms tools),
  // and both behaviors landing is fine.
  //
  // onClose rides a ref so the listener subscribes ONCE on mount and
  // never churns: the parent passes several inline-created props
  // (onHeartbeatKilled, composer) that change identity on nearly every
  // re-render, so a `[props]`/`[props.onClose]` dep would tear down and
  // re-add the window listener constantly — opening a window where an
  // Esc press can land while no listener is attached (the race the
  // coach-review focus-mode spec was flaking on under CI load).
  const onCloseRef = useRef(props.onClose);
  onCloseRef.current = props.onClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // The Tools toggle and its panel now live in the header /
  // anchored beneath the header — lift the open/close state up so
  // both stay in sync. Panel mount/unmount is preserved across
  // toggles (see FloatingToolPanel docstring).
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [playbackHidden, setPlaybackHidden] = useState(false);
  const [toolbarTarget, setToolbarTarget] = useState<HTMLDivElement | null>(
    null,
  );
  const [formationTarget, setFormationTarget] = useState<HTMLDivElement | null>(
    null,
  );
  const formationSlot = (content: React.ReactNode) =>
    formationTarget ? createPortal(content, formationTarget) : null;
  const toolbarSlot = (content: React.ReactNode) =>
    toolbarTarget
      ? createPortal(<ToolRail>{content}</ToolRail>, toolbarTarget)
      : null;

  // Mark moment / Add details are owned by the editor outside this portal.
  // Reflect that parent state in the Focus composer without coupling the
  // independently collapsible editor and composer controls.
  useEffect(() => {
    setDetailsOpen(props.composerOpen);
  }, [props.composerOpen]);

  // Focus chrome overlays the canvas, so the canvas uses the largest
  // uncropped 16:9 rectangle in the full viewport. `innerWidth` matters
  // when the document scrollbar is present: the approved prototype uses
  // 100vw and therefore still reaches the viewport edges.
  const VIDEO_ASPECT_W_OVER_H = 16 / 9;
  const availableH = Math.max(80, overlaySize.height);
  const availableW = Math.max(
    1,
    typeof window === "undefined" ? overlaySize.width : window.innerWidth,
  );
  const stageW = Math.max(
    1,
    Math.min(availableW, Math.floor(availableH * VIDEO_ASPECT_W_OVER_H)),
  );

  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      id="review-focus"
      ref={overlayRef}
      className="focus-overlay"
      data-testid="coach-review-focus-overlay"
      data-coach-review-portal="true"
      role="dialog"
      aria-modal="true"
      aria-label="Focus mode"
      onKeyDown={(event) => {
        if (event.key !== "Tab") return;
        const controls = [
          ...(overlayRef.current?.querySelectorAll<HTMLElement>(
            "button:not([disabled]),input:not([disabled]),textarea:not([disabled]),a[href],[tabindex='0']",
          ) ?? []),
        ].filter((el) => el.getClientRects().length && !el.closest("[hidden]"));
        const first = controls[0],
          last = controls.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }}
    >
      <div className="focus-canvas-area">
        {props.source === "tactical_board" ? (
          <TacticalBoardEditor
            appearance="navy"
            scene={props.scene}
            onSceneChange={props.onSceneChange}
            width={stageW}
            players={props.players}
            toolRailSlot={toolbarSlot}
            formationControlsSlot={formationSlot}
            compactFormationControls
          />
        ) : (
          <TelestratorCanvas
            drawing={props.drawing}
            onDrawingChange={props.onDrawingChange}
            width={stageW}
            match={props.match}
            slot={props.slot}
            videoRef={props.videoRef}
            onHeartbeatKilled={props.onHeartbeatKilled}
            toolRailSlot={toolbarSlot}
            showDrawing={props.showDrawing}
          />
        )}
      </div>
      <header className="focus-header">
        <div className="focus-context">
          <p className="eyebrow">Focus mode</p>
          <h2>
            {props.source === "video" && props.match
              ? `${props.match.home_team} vs. ${props.match.away_team}`
              : props.source === "tactical_board"
                ? "Tactical board"
                : "Video review"}
          </h2>
          <p className="focus-context-line">
            {props.source === "video"
              ? [
                  SLOT_LABELS[props.slot as MatchSlot] ?? props.slot,
                  props.teamName,
                ]
                  .filter(Boolean)
                  .join(" · ")
              : props.teamName}
          </p>
        </div>
        <div
          className="focus-source focus-source-toggle"
          role="group"
          aria-label="Focus review source"
        >
          {(["video", "tactical_board"] as const).map((source) => (
            <button
              key={source}
              type="button"
              aria-pressed={props.source === source}
              onClick={() => props.onSourceChange(source)}
            >
              {source === "video" ? "Video" : "Tactical board"}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={props.onClose}
          data-testid="coach-review-focus-exit"
          className="focus-exit"
        >
          <Minimize size={18} aria-hidden />
          Exit focus <kbd>Esc</kbd>
        </button>
      </header>
      <div className="focus-drawing-toolbar review-drawing-toolbar">
        <div ref={setToolbarTarget} />
      </div>
      <div id="focus-composer" hidden={!detailsOpen} className="focus-composer">
        <button
          type="button"
          className="focus-composer-close"
          aria-label="Close feedback details"
          title="Close feedback details"
          onClick={props.onComposerClose}
        >
          <X size={18} aria-hidden />
        </button>
        {props.composer}
      </div>
      {props.source === "video" ? (
        <footer
          className={`focus-footer${playbackHidden ? " is-editor-hidden" : ""}`}
        >
          <div className="focus-playback-dock">
            <button
              type="button"
              aria-expanded={!playbackHidden}
              aria-controls="focus-playback-editor"
              onClick={() => setPlaybackHidden((v) => !v)}
              data-testid="focus-playback-toggle"
              className={
                playbackHidden ? "focus-editor-show" : "focus-editor-hide"
              }
            >
              {playbackHidden ? (
                <>
                  <ChevronUp size={16} aria-hidden />
                  <span>Show video editor</span>
                </>
              ) : (
                <>
                  <ChevronDown size={18} aria-hidden />
                  <span className="sr-only">Hide video editor</span>
                </>
              )}
            </button>
            <div id="focus-playback-editor" hidden={playbackHidden}>
              {props.playbackEditor}
            </div>
          </div>
        </footer>
      ) : (
        <footer className="focus-footer focus-tactical-footer">
          <div className="focus-bottom-row">
            <div ref={setFormationTarget} />
            <button
              type="button"
              aria-expanded={detailsOpen}
              aria-controls="focus-composer"
              onClick={() => {
                if (detailsOpen) props.onComposerClose();
                else props.onComposerOpen();
              }}
              className="focus-details-toggle"
            >
              <MessageSquarePlus size={18} aria-hidden />
              {detailsOpen ? "Hide details" : "Mark observation"}
            </button>
          </div>
          <p className="focus-preview-label">
            Select · drag a drawing to move it
          </p>
        </footer>
      )}
    </div>,
    document.body,
  );
}

/** M9a A8 — auth gate inside the Coach Review shell. Watches the
 * bundle query for 401; on auth failure, redirects to
 * `/login?next=/coach/review`. Also gates on role: a signed-in user
 * with no coach access who deep-links here is bounced to `/hub` (the
 * role-based redirect) rather than left on a shell whose queries
 * would 403 — and rather than sent to `/login`, which would loop an
 * already-authenticated user. Renders nothing; the effect fires
 * before React paints so the user doesn't see a flash of empty
 * shell. Ref-flag prevents the effect re-firing after the URL
 * updates (TanStack Router keeps the parent mounted briefly). */
function CoachReviewAuthGate({
  bundle,
  me,
}: {
  bundle: ReturnType<typeof useCoachReviewBundle>;
  me: ReturnType<typeof useMe>;
}) {
  const navigate = useNavigate({ from: "/coach/review" });
  const isAuthError =
    bundle.error instanceof ApiError && bundle.error.status === 401;
  const isMeAuthError = me.error instanceof ApiError && me.error.status === 401;
  const isAnon = me.data === undefined ? false : me.data?.user == null;
  const showLogin = isAuthError || isMeAuthError || isAnon;

  // `next` captures the FULL path + search (`/coach/review?match_id=
  // …&note_id=…`) at mount via a ref so a coach deep-linked to a
  // specific match / note / clip resumes exactly there after signing
  // in, instead of an empty Coach Review surface. The ref freezes
  // the value before the redirect rewrites the URL; `window.location`
  // is the same source `PublicAppHeader` uses for its `loginNext`.
  const authGateMountPath = useRef(
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}` ||
          "/coach/review"
      : "/coach/review",
  );
  const authRedirectFired = useRef(false);

  useEffect(() => {
    if (!showLogin) return;
    if (authRedirectFired.current) return;
    authRedirectFired.current = true;
    void navigate({
      to: "/login",
      search: { next: authGateMountPath.current },
      replace: true,
    });
  }, [showLogin, navigate]);

  // Wrong-role gate — mirrors CoachShell, but this route is a
  // feedback-authoring surface. Team admins can open the operational
  // Coach workspace, but direct /coach/review bookmarks redirect back
  // to Matches instead of rendering a feedback tool they cannot use.
  const canOpenCoach = getPublicNavCapabilities(me.data).canOpenCoach;
  const canOpenCoachReview = canAuthorCoachFeedback(me.data);
  const roleRedirectFired = useRef(false);
  useEffect(() => {
    if (showLogin) return;
    if (!me.data?.user) return;
    if (canOpenCoach && canOpenCoachReview) return;
    if (roleRedirectFired.current) return;
    roleRedirectFired.current = true;
    void navigate({
      to: canOpenCoach ? "/coach" : "/hub",
      search: canOpenCoach ? { tab: "matches" } : undefined,
      replace: true,
    });
  }, [showLogin, me.data, canOpenCoach, canOpenCoachReview, navigate]);

  return null;
}
